<template>
  <div class="order-detail-page bg-gray-100 h-full w-full relative text-base">
    <!-- 顶部导航栏 -->
    <div
      class="bg-green-600 text-white px-3 py-2 flex items-center justify-between sticky top-0 z-10"
    >
      <div class="flex items-center">
        <router-link v-if="!detailOrder" to="/tableManage" class="text-white mr-3">
          <i class="fas fa-chevron-left"></i>
        </router-link>
        <h1 class="text-base sm:text-lg font-semibold">
          {{ shopName }} - {{ tableName }} - 订单
        </h1>
      </div>
      <div class="flex items-center space-x-3">
        <button
          v-if="currentView === 'ordered' && order.orderStatus === 1"
          class="bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg font-semibold transition-colors"
          @click="confirmOrderInDetails"
        >
          <i class="fas fa-check mr-2"></i>确认订单
        </button>
        <!-- <button
          v-if="canProceedToCheckout()"
          class="bg-green-500 hover:bg-green-600 px-4 py-2 rounded-lg font-semibold transition-colors"
          @click="proceedToCheckout"
        >
          <i class="fas fa-credit-card mr-2"></i>去结账
        </button> -->
        <button
          v-if="canShowConfirmPayment()"
          class="bg-green-500 hover:bg-green-600 px-4 py-2 rounded-lg font-semibold transition-colors mr-2"
          @click="confirmPaymentComplete"
        >
          <i class="fas fa-check-circle mr-2"></i>确认支付完成
        </button>
        <button
          v-if="isOnlinePayment && canShowConfirmPayment()"
          class="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-lg font-semibold transition-colors"
          @click="showScanPaymentModal"
        >
          <i class="fas fa-qrcode mr-2"></i>扫码支付
        </button>
        <button v-if="!detailOrder" class="mr-2" @click="showOrderHistory">
          <i class="fas fa-history text-white"></i>
        </button>
        <button>
          <i class="fas fa-ellipsis-v text-white"></i>
        </button>
      </div>
    </div>

    <!-- 当前订单信息 -->
    <div class="order-summary bg-white px-3 py-2 text-sm">
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-600">
        <span>订单号：{{ orderNo }}</span>
        <span>下单：{{ orderTime }}</span>
        <span v-if="appointmentTimeText" class="text-cyan-700 font-medium">
          预约时间：{{ appointmentTimeText }}
        </span>
        <span v-if="order.customerId">用户：{{ order.customerId }}</span>
      </div>
      <div class="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-600">
        <span>{{ order.dineType === 5 ? '预定' : '用餐' }}：{{ peopleCount }}人</span>
        <span>已点 {{ currentDishCount }}道 · 已上 {{ servedDishCount }}道</span>
        <span class="text-green-600 font-semibold">总计 ¥{{ currentTotal }}</span>
      </div>
    </div>

    <!-- 菜单和订单区域 -->
    <div class="flex flex-row min-h-[calc(100dvh-150px)]">
      <!-- 订单管理区域 -->
      <div class="w-full bg-white flex flex-col text-base">
        <!-- 视图切换标签 -->
        <div class="bg-gray-100 px-2 py-1 sticky top-0 z-20">
          <div class="flex bg-white rounded-lg p-1 shadow-sm">
            <button
              class="view-tab-btn flex-1 py-2 px-3 rounded-md text-center font-semibold transition-all"
              :class="{ active: currentView === 'ordered' }"
              @click="switchView('ordered')"
            >
              已点菜品 (<span>{{ orderedTabCount }}</span
              >)
            </button>
            <button
              class="view-tab-btn flex-1 py-2 px-3 rounded-md text-center font-semibold transition-all"
              :class="{ active: currentView === 'adding' }"
              @click="switchView('adding')"
            >
              加菜列表 (<span>{{ addingTabCount }}</span
              >)
            </button>
          </div>
        </div>

        <!-- 已点菜品视图 -->
        <div v-show="currentView === 'ordered'" class="view-content flex-1 flex flex-col">
          <div
            class="px-3 py-1 bg-blue-50 flex flex-wrap gap-2 justify-between items-center text-base border-b"
          >
            <div>
              <h2 class="font-bold text-blue-800">已点菜品管理</h2>
              <p v-if="kitchenStatusMessage" class="text-sm text-orange-700">
                {{ kitchenStatusMessage }}
              </p>
            </div>
            <div class="flex items-center space-x-3">
              <span class="text-sm text-blue-600"
                >共<span>{{ orderedCount }}</span
                >道菜</span
              >
              <div
                v-if="order.orderStatus !== 1 && hasOperableDishes"
                class="flex items-center space-x-2 border-l border-blue-300 pl-3"
              >
                <button
                  class="batch-operation-btn bg-red-500 hover:bg-red-600 text-white"
                  title="批量加急"
                  @click="batchUrgeDishes"
                >
                  <i class="fas fa-fire mr-1"></i>加急
                </button>
                <button
                  class="batch-operation-btn bg-gray-500 hover:bg-gray-600 text-white"
                  title="批量暂停"
                  :disabled="waiterPauseBusy || !currentOrderDishes.some(canWaiterPause)"
                  @click="batchPauseDishes"
                >
                  <i class="fas fa-pause mr-1"></i>暂停
                </button>
                <button
                  class="batch-operation-btn bg-green-500 hover:bg-green-600 text-white"
                  title="批量开始"
                  @click="batchResumeDishes"
                >
                  <i class="fas fa-play mr-1"></i>开始
                </button>
              </div>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto p-2 text-base">
            <div class="space-y-2">
              <div
                v-for="dish in currentOrderDishes"
                :key="dish.orderFoodId || dish.name"
                class="current-order-item px-3 py-2 rounded-r-lg"
                :class="getDishStatusClass(dish.status)"
              >
                <div class="order-dish-row">
                  <!-- 左侧：菜品信息 -->
                  <div class="order-dish-info min-w-0 text-left">
                    <div class="font-medium text-base break-words">{{ foodDisplayName(dish) }}</div>
                    <!-- 添加做法描述显示 -->
                    <div
                      v-if="dish.foodCookDesc && dish.foodCookDesc.trim() !== ''"
                      class="order-item-cook text-sm text-red-600 mt-1"
                    >
                      <i class="fas fa-utensils mr-1"></i>{{ dish.foodCookDesc }}
                    </div>
                    <div class="order-dish-labels mt-1 flex flex-wrap items-center gap-2 text-xs">
                      <span class="text-sm text-gray-600"
                        >¥{{ dish.price }} × {{ dish.quantity }}份</span
                      >
                      <div class="text-sm mt-1" :class="getDishStatusTextClass(dish.status)">
                        <i :class="getDishStatusIcon(dish.status)"></i>
                        {{ getDishStatusTextByCode(dish.originalStatus) }}
                      </div>
                      <!-- 操作状态显示 -->
                      <div
                        v-if="dish.operateStatus !== 'normal' || isDishUrgent(dish)"
                        class="text-xs mt-1"
                      >
                        <span
                          v-if="isDishUrgent(dish)"
                          class="inline-flex items-center px-2 py-1 rounded-full text-red-600 bg-red-100"
                        >
                          <i class="fas fa-fire mr-1"></i>已加急
                        </span>
                        <span
                          v-if="dish.operateStatus === 'paused'"
                          class="inline-flex items-center px-2 py-1 rounded-full text-red-600 bg-red-100"
                        >
                          <i class="fas fa-pause mr-1"></i>已暂停
                        </span>
                      </div>
                      <span
                        v-if="dishPriority(dish) === 666 && !isDishUrgent(dish)"
                        class="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs text-orange-700"
                        >已叫菜</span
                      >
                    </div>
                    <div class="order-kitchen-progress" aria-label="厨房制作状态">
                      <div
                        v-for="task in dishKitchenTasks(dish)"
                        :key="task.orderOutId"
                        class="order-kitchen-task"
                      >
                        <span
                          class="order-kitchen-status"
                          :data-status="
                            task.status === 999
                              ? 999
                              : task.kitchenStatus === 10
                              ? 10
                              : task.operateStatus === 1
                              ? 9
                              : task.kitchenStatus
                          "
                          >{{ kitchenTaskStatusText(task) }}</span
                        >
                        <span class="order-kitchen-assignee">
                          {{ task.kitchenName || '厨房' }} ·
                          {{
                            task.stoveName ||
                            (task.stoveId
                              ? '厨师待确认'
                              : task.kitchenStatus === 0
                              ? '未分配厨师'
                              : '厨师未记录')
                          }}
                          <span v-if="dishKitchenTasks(dish).length > 1">
                            · {{ task.quantity }}份</span
                          >
                        </span>
                      </div>
                      <span v-if="!dishKitchenTasks(dish).length" class="order-kitchen-assignee">
                        {{
                          kitchenStatusMessage
                            ? '厨房状态暂不可用'
                            : kitchenStatusReady
                            ? '暂无厨房任务'
                            : '正在读取厨房状态…'
                        }}
                      </span>
                    </div>
                  </div>
                  <!-- 中间：上菜按钮 -->
                  <div class="order-dish-serve flex justify-end">
                    <button
                      v-if="dish.status !== 'served' && dish.status !== 'cancelled'"
                      class="serve-btn-large"
                      title="上菜"
                      @click="serveDish(dish)"
                    >
                      <i class="fas fa-utensils mr-2"></i>上菜
                    </button>
                    <div
                      v-else-if="dish.status === 'served'"
                      class="served-status-badge"
                      title="已上菜"
                    >
                      <i class="fas fa-check-circle mr-2"></i>已上菜
                    </div>
                    <div
                      v-else-if="dish.status === 'cancelled'"
                      class="cancelled-status-badge"
                      title="已取消"
                    >
                      <i class="fas fa-times-circle mr-2"></i>已取消
                    </div>
                  </div>
                  <!-- 右侧：价格和操作 -->
                  <div class="order-dish-actions flex flex-col items-end gap-1">
                    <div class="font-semibold text-base">¥{{ dish.price * dish.quantity }}</div>
                    <div
                      v-if="canWaiterKitchenOperate(dish)"
                      class="flex flex-wrap justify-end gap-1"
                    >
                      <button
                        v-if="
                          dish.status !== 'served' &&
                          dish.status !== 'cancelled' &&
                          dish.originalStatus !== 8 &&
                          dish.operateStatus !== 'paused' &&
                          !isDishUrgent(dish) &&
                          dishPriority(dish) < 666 &&
                          order.orderStatus !== 1
                        "
                        class="text-orange-600 hover:text-orange-800"
                        title="叫菜"
                        @click="callDish(dish)"
                      >
                        <i class="fas fa-bullhorn"></i>
                      </button>
                      <!-- 加急按钮：只在正常状态且未上菜未取消且非已出餐时显示 -->
                      <button
                        v-if="
                          dish.status !== 'served' &&
                          dish.status !== 'cancelled' &&
                          !isDishUrgent(dish) &&
                          dish.operateStatus !== 'paused' &&
                          dish.originalStatus !== 8 &&
                          order.orderStatus !== 1
                        "
                        class="text-red-600 hover:text-red-800"
                        title="加急"
                        @click="urgeDish(dish)"
                      >
                        <i class="fas fa-bell"></i>
                      </button>
                      <!-- 服务员只能暂停尚未开工的菜品 -->
                      <button
                        v-if="canWaiterPause(dish) && order.orderStatus !== 1"
                        :disabled="waiterPauseBusy"
                        class="text-gray-600 hover:text-gray-800"
                        title="暂停"
                        @click="pauseDish(dish)"
                      >
                        <i class="fas fa-pause"></i>
                      </button>
                      <!-- 恢复按钮：只在暂停状态且非已出餐时显示 -->
                      <button
                        v-if="
                          dish.operateStatus === 'paused' &&
                          dish.status !== 'served' &&
                          dish.status !== 'cancelled' &&
                          dish.originalStatus !== 8 &&
                          order.orderStatus !== 1
                        "
                        class="text-green-600 hover:text-green-800"
                        title="开始"
                        @click="resumeDish(dish)"
                      >
                        <i class="fas fa-play"></i>
                      </button>
                      <!-- 已开工（含厨师暂停）或制作完成的菜品不可取消 -->
                      <button
                        v-if="canWaiterCancel(dish)"
                        :disabled="waiterCancelBusy"
                        class="text-red-600 hover:text-red-800"
                        title="取消"
                        @click="cancelDish(dish)"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 订单汇总 -->
          <div class="p-3 border-t bg-gray-50">
            <div class="space-y-2">
              <div class="flex justify-between text-lg">
                <span>小计</span>
                <span>¥{{ subtotal.toFixed(2) }}</span>
              </div>
              <div class="flex justify-between items-center text-lg">
                <div class="flex items-center">
                  <span>服务费 ({{ serviceFeeRate * 100 }}%)</span>
                  <!-- <button
                    @click="adjustServiceFee"
                    class="ml-2 text-green-500 hover:text-green-700 text-sm"
                    title="点击调整服务费"
                  >
                    <i class="fas fa-edit"></i>
                  </button> -->
                </div>
                <span>¥{{ serviceFee.toFixed(2) }}</span>
              </div>
              <div class="flex justify-between items-center text-lg">
                <div class="flex items-center">
                  <span>折扣金额</span>
                  <button
                    v-if="canEditDiscountAmount()"
                    class="ml-2 text-green-500 hover:text-green-700 text-sm"
                    title="点击编辑折扣金额"
                    @click="editDiscountAmount"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                </div>
                <span class="text-green-600">-¥{{ discountAmount.toFixed(2) }}</span>
              </div>
              <div class="flex justify-between font-bold text-xl border-t pt-2">
                <span>总计</span>
                <span>¥{{ total.toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 加菜视图 -->
        <div v-show="currentView === 'adding'" class="view-content flex-1">
          <div class="flex h-full">
            <!-- 左侧分类导航 -->
            <div class="w-1/4 bg-white overflow-y-auto border-r border-gray-200">
              <div class="sticky top-0 bg-gray-200 p-3">
                <div class="relative">
                  <input
                    v-model="searchKeyword"
                    type="text"
                    placeholder="搜索菜品"
                    class="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-lg"
                  />
                  <i class="fas fa-search absolute right-3 top-3 text-gray-400"></i>
                </div>
              </div>
              <div class="p-3">
                <div
                  v-for="category in categories"
                  :key="category.id"
                  class="category-item"
                  :class="{ active: selectedCategory === category.name }"
                  @click="selectCategory(category.name)"
                >
                  {{ category.name }}
                </div>
              </div>
            </div>

            <!-- 中间菜品列表 -->
            <div class="w-2/4 bg-white overflow-y-auto border-r border-gray-200">
              <div class="p-3 bg-gray-100 sticky top-0 z-10">
                <h2 class="font-bold text-xl">{{ selectedCategory }}</h2>
              </div>

              <div class="menu-items">
                <div
                  v-for="item in filteredMenuItems"
                  :key="item.id"
                  class="menu-item"
                  @click="showSpecDialog(item, $event)"
                >
                  <div class="menu-item-image">
                    <img :src="item.image" :alt="item.name" class="object-cover w-full h-full" />
                  </div>
                  <div class="menu-item-content">
                    <h3 class="menu-item-title">{{ item.name }}</h3>
                    <p class="menu-item-desc">{{ item.description }}</p>
                    <div class="flex justify-between items-center">
                      <span class="menu-item-price">¥{{ item.price }}</span>
                      <div class="flex items-center">
                        <button
                          v-if="getOrderItemCount(item.name) > 0"
                          class="menu-item-btn decrease mr-2"
                          @click.stop="decreaseItem(item.name)"
                          >-</button
                        >
                        <span
                          v-if="getOrderItemCount(item.name) > 0"
                          class="menu-item-count mr-2"
                          >{{ getOrderItemCount(item.name) }}</span
                        >
                        <button
                          class="menu-item-btn increase"
                          @click.stop="showSpecDialog(item, $event)"
                          >+</button
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 右侧加菜列表 -->
            <div ref="orderPanel" class="w-1/4 bg-white flex flex-col">
              <div
                class="p-3 bg-green-50 sticky top-0 z-10 flex justify-between items-center border-b"
              >
                <h2 class="font-bold text-green-800 text-xl">加菜列表</h2>
                <button class="text-red-500 text-sm hover:text-red-700" @click="clearOrder">
                  <i class="fas fa-trash-alt mr-1"></i> 清空
                </button>
              </div>

              <div class="flex-1 overflow-y-auto p-3">
                <div v-if="orderItems.length === 0" class="text-center text-gray-500 py-12">
                  <i class="fas fa-plus-circle text-4xl mb-4"></i>
                  <p class="text-lg">暂未选择加菜</p>
                  <p class="text-sm text-gray-400 mt-2">选择左侧菜品添加</p>
                </div>
                <div v-else>
                  <div v-for="item in orderItems" :key="item.name" class="order-item text-2xl">
                    <div class="order-item-info">
                      <div class="order-item-name">{{ item.name }}</div>
                      <div
                        v-if="
                          (item.specName && item.specName !== '标准') ||
                          (item.methodName && item.methodName.trim() !== '')
                        "
                        class="order-item-spec text-sm text-gray-500"
                      >
                        <template v-if="item.specName && item.specName !== '标准'">{{
                          item.specName
                        }}</template>
                        <template v-if="item.methodName && item.methodName.trim() !== ''">
                          {{ item.specName && item.specName !== '标准' ? ' - ' : ''
                          }}{{ item.methodName }}
                        </template>
                      </div>
                      <div class="order-item-price">¥{{ item.price }} × {{ item.quantity }}</div>
                    </div>
                    <div class="order-item-controls">
                      <button class="menu-item-btn decrease mr-2" @click="decreaseItem(item.name)"
                        >-</button
                      >
                      <span class="menu-item-count text-bold mr-2">{{ item.quantity }}</span>
                      <button class="menu-item-btn increase" @click="showSpecDialog(item, $event)"
                        >+</button
                      >
                    </div>
                  </div>
                </div>
              </div>

              <div class="p-3 border-t bg-gray-50">
                <div class="flex justify-between font-bold text-lg mb-3">
                  <span
                    >加菜总计
                    <span class="text-sm text-gray-500">（共{{ totalOrderCount }}份）</span></span
                  >
                  <span class="text-green-600">¥{{ orderTotal.toFixed(2) }}</span>
                </div>
                <button
                  class="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-bold text-xl transition-colors"
                  :disabled="orderItems.length === 0"
                  :class="{ 'opacity-50': orderItems.length === 0 }"
                  @click="submitAddOrder"
                >
                  提交加菜
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 规格选择弹窗 -->
    <div
      v-if="showSpecModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-modal="spec-modal"
    >
      <div class="bg-white rounded-xl p-6 w-80 max-w-md">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">选择规格</h3>
          <button @click="hideSpecModal">
            <i class="fas fa-times text-gray-500"></i>
          </button>
        </div>

        <!-- 菜品信息 -->
        <div class="mb-4 p-3 bg-gray-50 rounded-lg">
          <div class="flex items-center">
            <img
              :src="currentFood.image"
              :alt="currentFood.name"
              class="w-16 h-16 rounded-lg mr-3 object-cover"
            />
            <div>
              <h4 class="font-medium">{{ currentFood.name }}</h4>
              <p class="text-gray-600 text-sm">{{ currentFood.description }}</p>
            </div>
          </div>
        </div>

        <!-- 规格选择 -->
        <div class="mb-4">
          <h4 class="text-lg font-medium mb-3">选择规格</h4>
          <div class="space-y-2">
            <button
              v-for="spec in currentFood.specs || []"
              :key="spec.id"
              class="w-full p-3 rounded-lg border flex items-center justify-between"
              :class="
                selectedSpec.id === spec.id ? 'border-green-500 bg-green-50' : 'border-gray-300'
              "
              @click="selectedSpec = spec"
            >
              <span>{{ spec.name }}</span>
              <span class="font-medium">¥{{ spec.price }}</span>
              <i v-if="selectedSpec.id === spec.id" class="fas fa-check text-green-500"></i>
            </button>
          </div>
        </div>

        <!-- 做法选择 -->
        <div v-if="methods.length > 0" class="mb-4">
          <div
            v-for="(attributeGroup, attributeName) in groupedMethods"
            :key="attributeName"
            class="mb-4"
          >
            <h4 class="text-lg font-medium mb-3">选择{{ attributeName }}</h4>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="method in attributeGroup"
                :key="method.id"
                class="px-3 py-1 rounded-full border text-sm"
                :class="
                  isMethodSelected(attributeName, method.id)
                    ? 'border-green-500 bg-green-50 text-green-600'
                    : 'border-gray-300 text-gray-600'
                "
                @click="toggleMethodSelection(attributeName, method.id)"
              >
                {{ method.name }}
              </button>
            </div>
          </div>
        </div>

        <!-- 确认按钮 -->
        <div class="flex space-x-3">
          <button
            class="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            @click="hideSpecModal"
          >
            取消
          </button>
          <button
            class="flex-1 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            @click="confirmAddToOrder"
          >
            确认添加
          </button>
        </div>
      </div>
    </div>

    <!-- 折扣金额编辑模态框 -->
    <div
      v-if="discountModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl p-6 w-96 max-w-md max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">编辑折扣金额</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="cancelEditDiscount">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-4">
          <!-- 折扣金额输入 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"> 折扣金额（元） </label>
            <input
              type="text"
              :value="tempDiscountAmount"
              placeholder="请输入折扣金额"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-lg"
              @input="handleDiscountInput"
            />
          </div>

          <!-- 备注输入 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"> 备注信息 </label>
            <textarea
              v-model="tempDiscountRemark"
              placeholder="请输入备注信息（可选）"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base resize-none"
            ></textarea>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex space-x-3 pt-6 mt-6 border-t">
          <button
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            @click="cancelEditDiscount"
          >
            取消
          </button>
          <button
            class="flex-1 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
            @click="saveDiscountAmount"
          >
            保存
          </button>
        </div>
      </div>
    </div>

    <!-- 支付确认模态框 -->
    <div
      v-if="paymentConfirmModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl p-6 w-96 max-w-md max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">选择支付方式</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="hidePaymentConfirmModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-4">
          <!-- 支付方式选择 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"> 支付方式 </label>
            <select
              v-model="selectedPaymentMethod"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-lg"
            >
              <option v-for="method in paymentMethods" :key="method.value" :value="method.value">
                {{ method.name }}
              </option>
            </select>
          </div>

          <!-- 备注输入 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"> 备注信息 </label>
            <textarea
              v-model="paymentRemark"
              placeholder="请输入备注信息（可选）"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base resize-none"
            ></textarea>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex space-x-3 pt-6 mt-6 border-t">
          <button
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            @click="hidePaymentConfirmModal"
          >
            取消
          </button>
          <button
            class="flex-1 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
            @click="confirmPayment"
          >
            确认支付
          </button>
        </div>
      </div>
    </div>

    <!-- 扫码支付全屏界面 -->
    <div v-if="scanPaymentModalVisible" class="fixed inset-0 bg-black z-50 flex flex-col">
      <!-- 顶部导航栏 -->
      <div class="bg-black bg-opacity-80 text-white px-4 py-3 flex items-center justify-between">
        <button class="text-white" @click="hideScanPaymentModal">
          <i class="fas fa-times text-2xl"></i>
        </button>
        <h3 class="text-xl font-bold">扫码支付</h3>
        <div class="w-8"></div>
      </div>

      <!-- 摄像头扫描区域 -->
      <div class="flex-1 relative overflow-hidden w-full h-full">
        <div id="qr-reader" class="qr-reader-container w-full h-full"></div>

        <!-- 动态扫描线 -->
        <div v-if="isScanning" class="absolute inset-0 pointer-events-none z-20">
          <div class="scan-line"></div>
        </div>
      </div>

      <!-- 底部状态提示 -->
      <div v-if="scanStatus" class="bg-black bg-opacity-80 px-4 py-3">
        <div class="p-3 rounded-lg text-center" :class="scanStatusClass">
          <p class="text-sm font-medium">{{ scanStatus }}</p>
        </div>
      </div>
    </div>

    <!-- 扫码支付失败确认模态框 -->
    <div
      v-if="scanPaymentFailModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl p-6 w-96 max-w-md">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">支付确认</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="cancelScanPaymentConfirm">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="mb-6">
          <p class="text-gray-700 text-lg"> 扫码支付返回失败，请确认客户是否已经完成支付？ </p>
        </div>

        <!-- 操作按钮 -->
        <div class="flex space-x-3">
          <button
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            @click="cancelScanPaymentConfirm"
          >
            取消
          </button>
          <button
            :disabled="isRefreshingPayment"
            :class="[
              'flex-1 py-3 rounded-lg transition-colors font-medium',
              isRefreshingPayment
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600',
            ]"
            @click="confirmScanPaymentPaid"
          >
            <span v-if="isRefreshingPayment">
              <i class="fas fa-spinner fa-spin mr-2"></i>确认支付中
            </span>
            <span v-else>确认已经支付</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 确认对话框 -->
    <ConfirmDialog ref="confirmDialog" :message="confirmMessage" :title="confirmTitle" />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, nextTick, onUnmounted } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import { CategoryModel, CategoryItemModel } from '@/model/food';
  import { fetchCategoryItemList, foodUp, fetchCategoryTagList } from '@/service/food';
  import {
    auditOrder,
    getOrder,
    fetchAddFoodWithOrder,
    cancelOrderFood,
    changeOperateStatus,
    changePriority,
    editAmount,
  } from '@/service/order';
  import { showDialog, showToast as showVantToast } from 'vant';
  import { OrderDetailModel, OrderListItemModel } from '@/model/order';
  import { fetchShopList, fetchShopDetail } from '@/service/base';
  import { getOrderTableList, completeOrder, insertOrderPayment } from '@/service/order';
  import { OrderTableListItemModel } from '@/model/order';
  import { OrderPaymentModel } from '@/model/orderPayment';
  import { ShopDetailModel } from '@/model/shop';
  import dayjs from 'dayjs';
  import { dateTimeFormat } from '@/utils/utils';
  import ConfirmDialog from '@/components/ConfirmDialog.vue';
  import { scanPayment, refreshScanPayment } from '@/service/payment';
  import { Html5Qrcode } from 'html5-qrcode';
  import { useCurrentEmployee } from '@/composables/useCurrentEmployee';
  import { fetchOrderKitchenTasks } from '@/service/kitchen';
  import type { FoodOutDetailsListModel } from '@/model/kitchen';

  // 定义菜品项类型
  type DishItem = {
    name: string;
    price: number;
    quantity: number;
    status: 'served' | 'cooking' | 'pending' | 'paused' | 'cancelled';
    originalStatus: number; // 原始状态码
    operateStatus: 'normal' | 'paused' | 'urgent'; // 操作状态：正常、暂停、加急
    priority: number;
    foodSpecName?: string; // 菜品规格名称
    shopFoodId?: number;
    shopFoodSpecId?: number;
    orderFoodId?: number;
    foodCookDesc?: string; // 添加做法描述
  };

  const foodDisplayName = (dish: DishItem): string => {
    const spec = dish.foodSpecName?.trim();
    return spec && spec !== '标准' ? `${dish.name}（${spec}）` : dish.name;
  };

  const props = defineProps<{ detailOrder?: OrderListItemModel }>();
  const router = useRouter();
  const route = useRoute();
  const { employeeId, employeeName } = useCurrentEmployee();

  // 生成订单编号的辅助函数
  const generateOrderNo = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');
    const minute = String(now.getMinutes()).padStart(2, '0');
    const second = String(now.getSeconds()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0');
    return `${year}${month}${day}${hour}${minute}${second}${random}`;
  };

  // 基础数据
  const tableNumber = ref(0);
  const tableName = ref('');
  const shopId = ref(0);
  const shopName = ref('');
  const orderId = ref(0);
  const peopleCount = ref(4);
  const orderTime = ref('');
  const orderNo = ref('');
  const currentView = ref('ordered');
  const searchKeyword = ref('');
  const selectedCategory = ref('热销推荐');
  const orderPanel = ref<HTMLElement | null>(null);
  const order = ref<OrderDetailModel>({} as OrderDetailModel);

  // 服务费配置（可调整）
  const serviceFeeRate = ref(0); // 服务费比例，0表示无服务费
  const serviceFeeModalVisible = ref(false);
  const tempServiceFeeRate = ref(0);

  // 折扣金额配置
  const discountAmount = ref(0); // 折扣金额
  const discountModalVisible = ref(false);
  const tempDiscountAmount = ref(0);
  const tempDiscountRemark = ref('');

  // 支付确认模态框配置
  const paymentConfirmModalVisible = ref(false);
  const selectedPaymentMethod = ref(4); // 默认选择POS支付
  const paymentRemark = ref('');
  const paymentMethods = [
    { value: 4, name: 'POS支付' },
    { value: 5, name: '公司挂账' },
    { value: 7, name: '现金支付' },
  ];

  // 扫码支付相关状态
  const scanPaymentModalVisible = ref(false);
  const isScanning = ref(false);
  const manualScanCode = ref('');
  const scanPaymentRemark = ref('');
  const scanStatus = ref('');
  const scanStatusClass = ref('');
  const manualInputModalVisible = ref(false);
  const scanPaymentFailModalVisible = ref(false); // 扫码支付失败确认模态框
  const scanPaymentFailErrorMsg = ref(''); // 扫码支付失败错误信息
  const isRefreshingPayment = ref(false); // 是否正在刷新支付状态
  let html5QrCode: Html5Qrcode | null = null;

  // 规格选择相关状态
  const showSpecModalVisible = ref(false);
  const currentFood = ref<any>({});
  const selectedSpec = ref<any>({});
  const selectedMethod = ref(0);
  const selectedMethods = ref<{ [key: string]: number | number[] }>({}); // 支持多个属性选择
  const currentEvent = ref<Event | undefined>(undefined); // 保存原始点击事件
  const methods = ref<
    Array<{
      id: number;
      name: string;
      cookId?: number;
      cookName?: string;
      cookType?: number;
      attributeName?: string; // 新增属性名称字段
    }>
  >([]);

  // 分类数据
  const categories = ref<CategoryModel[]>([]);
  // 所有菜品的完整列表，用于左侧选择“全部”时展示
  const allMenuItems = ref<CategoryItemModel[]>([]);

  // 已点菜品数据 - 改为动态数据
  const currentOrderDishes = ref<DishItem[]>([]);
  const orderKitchenTasks = ref<FoodOutDetailsListModel[]>([]);
  const kitchenStatusReady = ref(false);
  const unstartedOrderFoodIds = ref<Set<number>>(new Set());
  const waiterPauseBusy = ref(false);
  const waiterCancelBusy = ref(false);
  const kitchenStatusMessage = ref('');
  let kitchenStatusLoading = false;
  let kitchenStatusDisposed = false;
  let kitchenStatusTimer: ReturnType<typeof setInterval> | undefined;

  const dishKitchenTasks = (dish: DishItem): FoodOutDetailsListModel[] =>
    dish.orderFoodId
      ? orderKitchenTasks.value.filter((task) => Number(task.orderFoodId) === dish.orderFoodId)
      : [];

  const kitchenTaskStatusText = (task: FoodOutDetailsListModel): string => {
    if (task.status === 999) return '已取消';
    if (task.kitchenStatus === 10) return '制作完成';
    if (task.operateStatus === 1) return '服务员暂停';
    if (task.kitchenStatus === 0) return task.stoveId ? '待制作' : '未分配';
    if (task.kitchenStatus === 5) return '制作中';
    if (task.kitchenStatus === 9) return '厨师暂停';
    return '厨房状态待确认';
  };

  const canWaiterPause = (dish: DishItem): boolean =>
    Boolean(
      dish.orderFoodId &&
        unstartedOrderFoodIds.value.has(dish.orderFoodId) &&
        (dish.status === 'pending' || dish.status === 'cooking') &&
        dish.operateStatus !== 'paused' &&
        ![8, 9, 999].includes(dish.originalStatus),
    );

  const canWaiterKitchenOperate = (dish: DishItem): boolean =>
    Boolean(
      kitchenStatusReady.value &&
        !kitchenStatusMessage.value &&
        dish.orderFoodId &&
        ![1, 10, 999].includes(order.value.orderStatus || 0) &&
        ![8, 9, 999].includes(dish.originalStatus) &&
        dish.status !== 'served' &&
        dish.status !== 'cancelled' &&
        dishKitchenTasks(dish).every(
          (task) => [0, 5, 9].includes(task.kitchenStatus) && ![8, 9, 999].includes(task.status),
        ),
    );

  const canWaiterCancel = (dish: DishItem): boolean =>
    Boolean(
      kitchenStatusReady.value &&
        !kitchenStatusMessage.value &&
        dish.orderFoodId &&
        ![1, 10, 999].includes(order.value.orderStatus || 0) &&
        ![8, 9, 999].includes(dish.originalStatus) &&
        dish.status !== 'cancelled' &&
        dishKitchenTasks(dish).every(
          (task) => task.kitchenStatus === 0 && ![8, 9, 999].includes(task.status),
        ),
    );

  const dishPriority = (dish: DishItem): number => {
    const kitchenTasks = dishKitchenTasks(dish);
    return kitchenTasks.length
      ? Math.max(...kitchenTasks.map((task) => task.priority || 1))
      : dish.priority;
  };

  const isDishUrgent = (dish: DishItem): boolean =>
    dishPriority(dish) >= 999 || dish.operateStatus === 'urgent';

  const loadKitchenMakingState = async (): Promise<boolean> => {
    if (!orderId.value || kitchenStatusLoading || kitchenStatusDisposed) return false;
    const requestedOrderId = orderId.value;
    kitchenStatusLoading = true;
    try {
      const kitchenTasks: FoodOutDetailsListModel[] = [];
      const unstartedIds = new Set<number>();
      const blockedIds = new Set<number>();
      const pageSize = 100;
      let pageNum = 1;
      let loadedCount = 0;
      while (!kitchenStatusDisposed && orderId.value === requestedOrderId) {
        const response = await fetchOrderKitchenTasks({
          pageNum,
          pageSize,
          data: { orderId: requestedOrderId },
        });
        if (kitchenStatusDisposed || orderId.value !== requestedOrderId) return false;
        if (response.code !== 200) throw new Error(response.msg || '读取制作状态失败');
        const list = response.result?.list || [];
        for (const task of list) {
          if (Number(task.orderId) !== requestedOrderId) continue;
          kitchenTasks.push(task);
          const foodId = Number(task.orderFoodId);
          if (
            task.kitchenStatus === 0 &&
            task.operateStatus !== 1 &&
            ![8, 9, 999].includes(task.status)
          )
            unstartedIds.add(foodId);
          else blockedIds.add(foodId);
        }
        loadedCount += list.length;
        if (list.length < pageSize || loadedCount >= response.result.total) break;
        pageNum++;
      }
      orderKitchenTasks.value = kitchenTasks;
      kitchenStatusReady.value = true;
      unstartedOrderFoodIds.value = new Set(
        [...unstartedIds].filter((foodId) => !blockedIds.has(foodId)),
      );
      kitchenStatusMessage.value = '';
      return true;
    } catch (error) {
      console.error('读取订单菜品制作状态失败', error);
      if (!kitchenStatusDisposed && orderId.value === requestedOrderId) {
        orderKitchenTasks.value = [];
        kitchenStatusReady.value = false;
        unstartedOrderFoodIds.value = new Set();
        kitchenStatusMessage.value = '制作状态暂时无法读取，正在重试';
      }
      return false;
    } finally {
      kitchenStatusLoading = false;
    }
  };

  // 加菜订单数据
  const orderItems = ref<
    Array<{
      id: number;
      name: string;
      price: number;
      quantity: number;
      shopFoodSpecId: number;
      specName?: string;
      methodName?: string;
    }>
  >([]);

  // 计算属性
  const filteredMenuItems = computed(() => {
    let items: any[] = [];

    if (selectedCategory.value === '全部') {
      // 如果是"全部"分类，直接展示所有菜品（不再通过标签/分类拼接）
      items = allMenuItems.value;
    } else {
      const selectedCat = categories.value.find((cat) => cat.name === selectedCategory.value);
      items = selectedCat?.items || [];
    }

    if (!searchKeyword.value) return items;
    return items.filter(
      (item) =>
        item.name?.includes(searchKeyword.value) || item.description?.includes(searchKeyword.value),
    );
  });

  const currentDishCount = computed(() => {
    return currentOrderDishes.value
      .filter((dish) => dish.status !== 'cancelled')
      .reduce((sum, dish) => sum + dish.quantity, 0);
  });

  const servedDishCount = computed(() => {
    return currentOrderDishes.value
      .filter((dish) => dish.status === 'served')
      .reduce((sum, dish) => sum + dish.quantity, 0);
  });

  const subtotal = computed(() => {
    return currentOrderDishes.value
      .filter((dish) => dish.status !== 'cancelled')
      .reduce((sum, dish) => sum + dish.price * dish.quantity, 0);
  });

  const serviceFee = computed(() => Math.round(subtotal.value * serviceFeeRate.value));
  const total = computed(() =>
    Math.max(0, subtotal.value + serviceFee.value - discountAmount.value),
  );
  const currentTotal = computed(() => total.value.toFixed(2));

  const orderedTabCount = computed(() => currentDishCount.value);
  const addingTabCount = computed(() =>
    orderItems.value.reduce((sum, item) => sum + item.quantity, 0),
  );
  const orderedCount = computed(() => currentOrderDishes.value.length);

  const orderTotal = computed(() => {
    return orderItems.value.reduce((sum, item) => sum + item.price * item.quantity, 0);
  });

  const totalOrderCount = computed(() => {
    return orderItems.value.reduce((sum, item) => sum + item.quantity, 0);
  });

  const appointmentTimeText = computed(() => {
    const date = order.value.selfPickDate?.trim() || '';
    const time = order.value.selfPickTime?.trim() || '';
    return [date, time].filter(Boolean).join(' ');
  });

  // 检查是否有可操作的菜品（用于批量操作按钮显示）
  const hasOperableDishes = computed(() => {
    return currentOrderDishes.value.some(canWaiterKitchenOperate);
  });

  // 方法
  const getOrderItemCount = (name: string) => {
    const found = orderItems.value.find((item) => item.name === name);
    return found ? found.quantity : 0;
  };

  const getDishStatusClass = (status: string) => {
    const classes: Record<string, string> = {
      served: 'border-l-4 border-green-500 bg-green-50',
      cooking: 'border-l-4 border-yellow-500 bg-yellow-50',
      pending: 'border-l-4 border-blue-500 bg-blue-50',
      paused: 'border-l-4 border-red-500 bg-red-50',
      cancelled: 'border-l-4 border-gray-500 bg-gray-100',
    };
    return classes[status] || classes.pending;
  };

  const getDishStatusTextClass = (status: string) => {
    const classes: Record<string, string> = {
      served: 'text-green-600',
      cooking: 'text-yellow-600',
      pending: 'text-blue-600',
      paused: 'text-red-600',
      cancelled: 'text-gray-600',
    };
    return classes[status] || classes.pending;
  };

  const getDishStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      served: 'fas fa-check-circle mr-1',
      cooking: 'fas fa-clock mr-1',
      pending: 'fas fa-hourglass-half mr-1',
      paused: 'fas fa-pause-circle mr-1',
      cancelled: 'fas fa-times-circle mr-1',
    };
    return icons[status] || icons.pending;
  };

  // 根据原始状态码获取更准确的状态文本
  const getDishStatusTextByCode = (statusCode: number) => {
    switch (statusCode) {
      case 1:
        return '已点餐';
      case 2:
        return '待出餐';
      case 8:
        return '已出餐';
      case 9:
        return '已上菜';
      case 999:
        return '已取消';
      default:
        return '已点餐';
    }
  };

  const getDishStatusText = (status: string) => {
    const texts: Record<string, string> = {
      served: '已上菜',
      cooking: '待出餐',
      pending: '已点餐',
      paused: '已暂停',
      cancelled: '已取消',
    };
    return texts[status] || texts.pending;
  };

  const selectCategory = (name: string | undefined) => {
    selectedCategory.value = name || '';
  };

  // 加载订单详情数据
  const loadOrderDetail = async () => {
    try {
      if (!orderId.value) {
        console.error('订单ID为空');
        return;
      }

      const response = await getOrder({ orderId: orderId.value });
      if (response.code === 200 && response.result) {
        const orderData = response.result;

        // 更新基础信息
        orderTime.value = orderData.createTime
          ? typeof orderData.createTime === 'string'
            ? orderData.createTime
            : orderData.createTime.toString()
          : '';
        orderNo.value = orderData.orderNo || '';
        peopleCount.value = orderData.personCount || 4;

        // 获取折扣金额
        discountAmount.value = orderData.discountAmount || 0;

        // 转换菜品数据
        if (orderData.foodList && Array.isArray(orderData.foodList)) {
          currentOrderDishes.value = orderData.foodList.map((food: any) => ({
            name: food.foodName || food.shopFoodName || '',
            price: food.price || 0,
            quantity: food.quantity || 1,
            status: mapFoodStatus(food.status || food.foodStatus || 0),
            originalStatus: food.status || food.foodStatus || 0, // 保存原始状态码
            operateStatus: mapOperateStatus(food.operateStatus || food.foodOperateStatus || 0),
            priority: food.priority || 1,
            foodSpecName: food.foodSpecName,
            shopFoodId: food.shopFoodId,
            shopFoodSpecId: food.shopFoodSpecId,
            orderFoodId: food.orderFoodId,
            foodCookDesc: food.foodCookDesc, // 添加做法描述
          }));
        }
        order.value = orderData;
        await loadKitchenMakingState();

        console.log('订单详情加载成功:', orderData);
      } else {
        showDialog({ message: response.msg || '加载订单详情失败' });
      }
    } catch (error) {
      console.error('加载订单详情失败:', error);
      showDialog({ message: '加载订单详情失败，请稍后重试' });
    }
  };

  // 映射菜品状态
  const mapFoodStatus = (
    status: number,
  ): 'served' | 'cooking' | 'pending' | 'paused' | 'cancelled' => {
    switch (status) {
      case 1:
        return 'pending'; // 已点餐
      case 2:
        return 'cooking'; // 待出餐
      case 8:
        return 'cooking'; // 已出餐(可以上菜)
      case 9:
        return 'served'; // 已上菜
      case 999:
        return 'cancelled'; // 已取消
      default:
        return 'pending';
    }
  };

  const mapOperateStatus = (status: number): 'normal' | 'paused' | 'urgent' => {
    switch (status) {
      case 0:
        return 'normal'; // 正常
      case 1:
        return 'paused'; // 暂停
      case 10:
        return 'urgent'; // 加急
      default:
        return 'normal';
    }
  };

  const showSpecDialog = (item: any, event?: Event) => {
    console.log('=== showSpecDialog 调试信息 ===');
    console.log('当前菜品:', item.name);
    console.log('cookList:', item.cookList);
    console.log('cookList长度:', item.cookList?.length);

    currentFood.value = item;
    // 设置默认规格（第一个规格）
    if (item.specs && item.specs.length > 0) {
      selectedSpec.value = item.specs[0];
    } else {
      selectedSpec.value = { id: item.id, name: '标准', price: item.price };
    }

    // 从item.cookList动态获取做法选项
    if (item.cookList && item.cookList.length > 0) {
      console.log('开始解析cookList...');
      methods.value = [];

      item.cookList.forEach((cook: any, cookIndex: number) => {
        console.log(`处理第${cookIndex + 1}个cook:`, cook);
        console.log('cookValue:', cook.cookValue);
        console.log('valueList:', cook.valueList);

        // 解析cookValue JSON字符串
        let cookValues = [];
        try {
          if (cook.cookValue) {
            cookValues = JSON.parse(cook.cookValue);
            console.log('解析cookValue成功:', cookValues);
          } else if (cook.valueList && cook.valueList.length > 0) {
            // 如果cookValue为空，使用valueList
            cookValues = cook.valueList;
            console.log('使用valueList:', cookValues);
          }
        } catch (error) {
          console.error('解析cookValue失败:', error);
          // 如果解析失败，使用valueList作为备用
          if (cook.valueList && cook.valueList.length > 0) {
            cookValues = cook.valueList;
            console.log('解析失败，使用valueList备用:', cookValues);
          }
        }

        // 将解析出的值添加到methods中
        cookValues.forEach((valueItem: any, valueIndex: number) => {
          const value = valueItem.value || valueItem;
          const methodItem = {
            id: cook.cookId * 1000 + valueIndex, // 使用cookId * 1000 + valueIndex作为唯一ID
            name: value,
            cookId: cook.cookId,
            cookName: cook.cookName, // 属性名称，如"做法"、"配菜"
            cookType: cook.cookType,
            attributeName: cook.cookName, // 添加属性名称字段
          };
          methods.value.push(methodItem);
          console.log('添加做法选项:', methodItem);
        });
      });

      console.log('最终methods数组:', methods.value);
      // 设置默认选择第一个做法
      selectedMethod.value = 0;

      // 初始化每个属性的默认选择
      selectedMethods.value = {};
      // 默认不选任何做法（包括常规），无需初始化条目
    } else {
      console.log('没有cookList，使用默认做法（不默认选中）');
      // 如果没有cookList，提供一个"常规"选项，但不默认选中
      methods.value = [{ id: 0, name: '常规' }];
      selectedMethod.value = 0;
      selectedMethods.value = {};
    }

    currentEvent.value = event; // 保存原始点击事件

    // 如果有做法选择，或者有多个规格，都需要弹窗
    if ((item.cookList && item.cookList.length > 0) || (item.specs && item.specs.length > 1)) {
      showSpecModalVisible.value = true;
    } else {
      // 没有做法且只有一个规格，直接添加到订单
      addToOrderWithSpec(item, event);
    }
  };

  const hideSpecModal = () => {
    showSpecModalVisible.value = false;
    currentFood.value = {};
    selectedSpec.value = {};
    selectedMethod.value = 0;
    selectedMethods.value = {}; // 清空多属性选择
    methods.value = []; // 清空做法列表
    currentEvent.value = undefined; // 清空原始点击事件
  };

  const confirmAddToOrder = () => {
    console.log('=== 开始确认添加流程 ===');

    // 添加到订单
    addToOrderWithSpec(currentFood.value, currentEvent.value);

    // 立即开始动画，不等待DOM更新
    setTimeout(() => {
      flyFromSpecModal();
    }, 10);

    // 延长弹窗关闭时间，确保动画可见
    setTimeout(() => {
      hideSpecModal();
    }, 800);
  };

  const flyFromSpecModal = () => {
    console.log('=== 开始飞入动画 ===');
    console.log('当前菜品:', currentFood.value.name);

    // 直接查找当前显示的规格弹窗
    const specModal =
      document.querySelector('[data-modal="spec-modal"]') ||
      document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50:last-child');

    if (!specModal) {
      console.log('❌ 未找到规格弹窗');
      // 尝试备用方案
      const allModals = document.querySelectorAll('.fixed.inset-0.bg-black.bg-opacity-50');
      console.log('找到的所有弹窗:', allModals.length);
      if (allModals.length > 0) {
        const lastModal = allModals[allModals.length - 1];
        console.log('使用最后一个弹窗:', lastModal);
        startAnimation(lastModal);
      }
      return;
    }

    console.log('✅ 找到规格弹窗');
    startAnimation(specModal);
  };

  const startAnimation = (modal: Element) => {
    const img = modal.querySelector('img');
    if (!img) {
      console.log('❌ 未找到图片元素');
      return;
    }
    console.log('✅ 找到图片元素');

    if (!orderPanel.value) {
      console.log('❌ 未找到订单面板');
      return;
    }
    console.log('✅ 找到订单面板');

    const imgRect = img.getBoundingClientRect();
    const orderRect = orderPanel.value.getBoundingClientRect();

    console.log('图片位置:', {
      x: imgRect.left,
      y: imgRect.top,
      w: imgRect.width,
      h: imgRect.height,
    });
    console.log('订单面板位置:', {
      x: orderRect.left,
      y: orderRect.top,
      w: orderRect.width,
      h: orderRect.height,
    });

    // 确保图片有有效的位置信息
    if (imgRect.width === 0 || imgRect.height === 0) {
      console.log('❌ 图片尺寸无效');
      return;
    }

    // 创建克隆图片
    const clone = img.cloneNode(true) as HTMLImageElement;
    clone.style.position = 'fixed';
    clone.style.left = imgRect.left + 'px';
    clone.style.top = imgRect.top + 'px';
    clone.style.width = imgRect.width + 'px';
    clone.style.height = imgRect.height + 'px';
    clone.style.zIndex = '10000';
    clone.style.borderRadius = '8px';
    clone.style.boxShadow = '0 8px 24px rgba(0,0,0,0.5)';
    clone.style.transition = 'all 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '1';
    clone.style.border = '3px solid #10b981';
    document.body.appendChild(clone);
    console.log('✅ 克隆图片已添加');

    // 计算目标位置
    const targetX = orderRect.left + orderRect.width / 2 - imgRect.width / 2;
    const targetY = orderRect.top + 50;

    console.log('目标位置:', { x: targetX, y: targetY });

    // 开始动画
    setTimeout(() => {
      clone.style.transform = `translate(${targetX - imgRect.left}px, ${
        targetY - imgRect.top
      }px) scale(0.2)`;
      clone.style.opacity = '0.3';
      console.log('🚀 动画已开始');
    }, 100);

    // 清理
    setTimeout(() => {
      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
        console.log('🧹 动画完成，已清理');
      }
    }, 1600);
  };

  const addToOrderWithSpec = (item: any, event?: Event) => {
    // 使用选中的规格信息
    const specInfo = selectedSpec.value;

    // 生成组合的methodName，包含所有选择的属性（支持多选）
    const methodNames: string[] = [];
    // 按照methods数组的显示顺序来构建methodName
    const attributeGroups: { [key: string]: string[] } = {};

    // 先收集所有选中的做法，按属性分组
    Object.keys(selectedMethods.value).forEach((attributeName) => {
      const selectedIds = selectedMethods.value[attributeName];
      if (Array.isArray(selectedIds)) {
        // 多选：收集选中的做法名称
        const selectedMethodNames = selectedIds
          .map((id) => {
            const method = methods.value.find((m) => m.id === id);
            return method ? method.name : '';
          })
          .filter((name) => name); // 过滤掉空值

        if (selectedMethodNames.length > 0) {
          attributeGroups[attributeName] = selectedMethodNames;
        }
      } else if (selectedIds) {
        // 单选：收集选中的做法名称
        const method = methods.value.find((m) => m.id === selectedIds);
        if (method) {
          attributeGroups[attributeName] = [method.name];
        }
      }
    });

    // 按照methods数组中的显示顺序来构建最终的methodName
    methods.value.forEach((method) => {
      const attributeName = method.attributeName || method.cookName || '做法';
      if (attributeGroups[attributeName] && attributeGroups[attributeName].includes(method.name)) {
        // 如果这个做法被选中了，添加到结果中
        if (!methodNames.includes(`${attributeName}:${method.name}`)) {
          methodNames.push(`${attributeName}:${method.name}`);
        }
      }
    });

    // 按照属性分组重新组织显示格式
    const groupedMethods: { [key: string]: string[] } = {};

    // 将选中的做法按属性分组
    methodNames.forEach((methodName) => {
      const [attributeName, methodValue] = methodName.split(':');
      if (!groupedMethods[attributeName]) {
        groupedMethods[attributeName] = [];
      }
      groupedMethods[attributeName].push(methodValue);
    });

    // 按照属性分组构建最终的显示文本
    const finalMethodNames: string[] = [];
    Object.keys(groupedMethods).forEach((attributeName) => {
      const methods = groupedMethods[attributeName];
      if (methods.length > 0) {
        finalMethodNames.push(`${attributeName}：${methods.join('，')}`);
      }
    });

    const combinedMethodName = finalMethodNames.join('，');

    const orderItem = {
      ...item,
      price: specInfo.price || item.price,
      shopFoodSpecId: specInfo.id || item.id,
      specName: specInfo.name || '标准',
      methodId: selectedMethod.value,
      methodName: combinedMethodName,
    };

    // 1. 订单逻辑
    const found = orderItems.value.find(
      (o) => o.id === item.id && o.shopFoodSpecId === orderItem.shopFoodSpecId,
    );
    if (found) {
      found.quantity += 1;
    } else {
      orderItems.value.push({
        ...orderItem,
        quantity: 1,
        shopFoodSpecId: orderItem.shopFoodSpecId,
      });
    }

    // 2. 动画效果 - 只有在直接点击菜品时才播放flyToOrder动画
    // 如果是从规格弹窗确认添加，则不播放这个动画（会由flyFromSpecModal处理）
    if (event && !showSpecModalVisible.value) {
      nextTick(() => {
        flyToOrder(event);
      });
    }

    console.log('添加到加菜列表:', orderItem);
  };

  const flyToOrder = (event: any) => {
    console.log('开始飞入动画', event);

    const menuItem = event.target.closest('.menu-item');
    if (!menuItem) {
      console.log('未找到menu-item元素');
      return;
    }

    const img = menuItem.querySelector('img');
    if (!img) {
      console.log('未找到图片元素');
      return;
    }

    if (!orderPanel.value) {
      console.log('未找到订单面板元素');
      return;
    }

    const imgRect = img.getBoundingClientRect();
    const orderRect = orderPanel.value.getBoundingClientRect();

    console.log('图片位置:', imgRect);
    console.log('订单面板位置:', orderRect);

    const clone = img.cloneNode(true) as HTMLImageElement;
    clone.style.position = 'fixed';
    clone.style.left = imgRect.left + 'px';
    clone.style.top = imgRect.top + 'px';
    clone.style.width = imgRect.width + 'px';
    clone.style.height = imgRect.height + 'px';
    clone.style.zIndex = '9999';
    clone.style.borderRadius = '8px';
    clone.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
    clone.style.transition = 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '1';
    document.body.appendChild(clone);

    // 查找订单区中该商品的元素
    let orderTarget = null;
    const orderItemsElements = orderPanel.value.querySelectorAll('.order-item');
    orderItemsElements.forEach((orderItem: any) => {
      const orderName = orderItem.querySelector('.order-item-name');
      if (
        orderName &&
        orderName.textContent ===
          (event.target.closest('.menu-item')?.querySelector('.menu-item-title')?.textContent || '')
      ) {
        orderTarget = orderItem;
      }
    });

    let targetX, targetY;
    if (orderTarget) {
      // 已有该商品，飞向该商品
      const orderItemRect = (orderTarget as HTMLElement).getBoundingClientRect();
      targetX = orderItemRect.left + orderItemRect.width / 2 - imgRect.width / 2;
      targetY = orderItemRect.top + orderItemRect.height / 2 - imgRect.height / 2;
    } else {
      // 没有该商品，飞向订单区顶部
      targetX = orderRect.left + orderRect.width / 2 - imgRect.width / 2;
      targetY = orderRect.top + 30;
    }

    nextTick(() => {
      console.log('目标位置:', { targetX, targetY });

      clone.style.transform = `translate(${targetX - imgRect.left}px, ${
        targetY - imgRect.top
      }px) scale(0.3)`;
      clone.style.opacity = '0.5';
    });

    setTimeout(() => {
      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
      console.log('动画完成，移除克隆元素');
    }, 1200);
  };

  const switchView = (viewType: string) => {
    currentView.value = viewType;
    if (viewType === 'adding') {
      // 只有在分类数据未加载时才加载，避免重复请求
      if (categories.value.length === 0) {
        setTimeout(() => {
          loadAllCategories();
        }, 100);
      }
    }
  };

  const serveDish = async (dish: DishItem) => {
    confirmMessage.value = `确定要标记 ${dish.name} 为已上菜吗？`;
    confirmTitle.value = '确认上菜';
    if (await confirmDialog.value?.show()) {
      if (!dish.orderFoodId) {
        showToast('菜品信息不完整，无法上菜');
        return;
      }

      try {
        // 调用上菜接口
        const response = await foodUp({
          orderId: orderId.value,
          orderFoodId: dish.orderFoodId,
          shopId: shopId.value,
        });

        if (response.code === 200) {
          // 更新本地状态
          dish.status = 'served';
          showServingSuccess(dish.name);

          // 重新加载订单详情以获取最新状态
          await loadOrderDetail();
        } else {
          showToast(response.msg || '上菜失败，请稍后重试');
        }
      } catch (error) {
        console.error('上菜失败:', error);
        showToast('上菜失败，请稍后重试');
      }
    }
  };

  const callDish = async (dish: DishItem) => {
    if (!canWaiterKitchenOperate(dish)) {
      showToast('菜品已制作完成或状态未确认，不能叫菜');
      return;
    }
    confirmMessage.value = `确定要叫菜 ${dish.name} 吗？`;
    confirmTitle.value = '确认叫菜';
    if (!(await confirmDialog.value?.show())) return;
    try {
      if (!(await loadKitchenMakingState()) || !canWaiterKitchenOperate(dish)) {
        showToast('菜品已制作完成或状态未确认，不能叫菜');
        return;
      }
      const response = await changePriority({
        orderId: orderId.value,
        orderFoodId: dish.orderFoodId,
        shopId: shopId.value,
        priority: 666,
      });
      if (response.code !== 200) throw new Error(response.msg || '叫菜失败');
      showToast(`${dish.name} 已叫菜`);
    } catch (error) {
      console.error('叫菜失败:', error);
      showToast(error instanceof Error ? error.message : '叫菜失败，请稍后重试');
    } finally {
      await loadOrderDetail();
    }
  };

  const sendUrgent = async (dish: DishItem): Promise<void> => {
    if (!canWaiterKitchenOperate(dish)) throw new Error('菜品已制作完成或状态未确认，不能加急');
    const priorityResponse = await changePriority({
      orderId: orderId.value,
      orderFoodId: dish.orderFoodId,
      shopId: shopId.value,
      priority: 999,
    });
    if (priorityResponse.code !== 200) throw new Error(priorityResponse.msg || '加急失败');
  };

  const urgeDish = async (dish: DishItem) => {
    if (!canWaiterKitchenOperate(dish)) {
      showToast('菜品已制作完成或状态未确认，不能加急');
      return;
    }
    // 检查订单状态
    if (order.value.orderStatus === 1) {
      showToast('订单尚未确认，无法进行加急操作');
      return;
    }

    confirmMessage.value = `确定要加急 ${dish.name} 吗？`;
    confirmTitle.value = '确认加急';
    if (await confirmDialog.value?.show()) {
      try {
        if (!(await loadKitchenMakingState()) || !canWaiterKitchenOperate(dish)) {
          showToast('菜品已制作完成或状态未确认，不能加急');
          return;
        }
        await sendUrgent(dish);
        showToast(`${dish.name} 已成功加急`);
      } catch (error) {
        console.error('加急失败:', error);
        showToast(error instanceof Error ? error.message : '加急失败，请稍后重试');
      } finally {
        await loadOrderDetail();
      }
    }
  };

  const pauseDish = async (dish: DishItem): Promise<void> => {
    if (waiterPauseBusy.value) return;
    if (order.value.orderStatus === 1) {
      showToast('订单尚未确认，无法进行暂停操作');
      return;
    }
    if (!canWaiterPause(dish)) {
      showToast('服务员只能暂停尚未开工的菜品');
      return;
    }
    waiterPauseBusy.value = true;
    try {
      confirmMessage.value = `确定要暂停尚未开工的 ${dish.name} 吗？`;
      confirmTitle.value = '确认暂停';
      if (!(await confirmDialog.value?.show())) return;
      if (!(await loadKitchenMakingState())) {
        showToast('制作状态未确认，请稍后再试');
        return;
      }
      if (!canWaiterPause(dish)) {
        showToast('菜品已开工或状态已变化，不能暂停');
        return;
      }
      const response = await changeOperateStatus({
        orderId: orderId.value,
        orderFoodId: dish.orderFoodId,
        shopId: shopId.value,
        operateStatus: 1,
      });
      if (response.code === 200) {
        showToast(`${dish.name} 已暂停`);
        await loadOrderDetail();
      } else {
        showToast(response.msg || '暂停失败，请稍后重试');
      }
    } catch (error) {
      console.error('暂停失败:', error);
      showToast('暂停失败，请稍后重试');
    } finally {
      waiterPauseBusy.value = false;
    }
  };

  const resumeDish = async (dish: DishItem) => {
    if (!canWaiterKitchenOperate(dish)) {
      showToast('菜品已制作完成或状态未确认，不能开始');
      return;
    }
    // 检查订单状态
    if (order.value.orderStatus === 1) {
      showToast('订单尚未确认，无法进行恢复操作');
      return;
    }

    confirmMessage.value = `确定要开始 ${dish.name} 吗？`;
    confirmTitle.value = '确认开始';
    if (await confirmDialog.value?.show()) {
      try {
        if (!(await loadKitchenMakingState()) || !canWaiterKitchenOperate(dish)) {
          showToast('菜品已制作完成或状态未确认，不能开始');
          return;
        }
        const response = await changeOperateStatus({
          orderId: orderId.value,
          orderFoodId: dish.orderFoodId,
          shopId: shopId.value,
          operateStatus: 0, // Swagger：0 表示正常
        });

        if (response.code === 200) {
          // 更新本地状态
          dish.operateStatus = 'normal';
          showToast(`${dish.name} 已开始`);

          // 重新加载订单详情以获取最新状态
          await loadOrderDetail();
        } else {
          showToast(response.msg || '恢复失败，请稍后重试');
        }
      } catch (error) {
        console.error('恢复失败:', error);
        showToast('恢复失败，请稍后重试');
      }
    }
  };

  const cancelDish = async (dish: DishItem): Promise<void> => {
    if (waiterCancelBusy.value) return;
    if (!canWaiterCancel(dish)) {
      showToast('菜品已开工、制作完成或状态未确认，不能取消');
      return;
    }
    waiterCancelBusy.value = true;
    try {
      confirmMessage.value = `确定要取消 ${dish.name} 吗？此操作不可撤销！`;
      confirmTitle.value = '确认取消';
      if (!(await confirmDialog.value?.show())) return;
      if (!(await loadKitchenMakingState())) {
        showToast('制作状态未确认，请稍后再试');
        return;
      }
      if (!canWaiterCancel(dish)) {
        showToast('菜品已开工、制作完成或状态已变化，不能取消');
        return;
      }
      const response = await cancelOrderFood({
        orderId: orderId.value,
        orderFoodId: dish.orderFoodId,
        shopId: shopId.value,
      });
      if (response.code === 200) {
        showToast(`${dish.name} 已取消`);
        await loadOrderDetail();
      } else {
        showToast(response.msg || '取消失败，请稍后重试');
      }
    } catch (error) {
      console.error('取消菜品失败:', error);
      showToast('取消失败，请稍后重试');
    } finally {
      waiterCancelBusy.value = false;
    }
  };

  const batchUrgeDishes = async () => {
    // 检查订单状态
    if (order.value.orderStatus === 1) {
      showToast('订单尚未确认，无法进行批量加急操作');
      return;
    }

    // 筛选可以加急的菜品：未暂停、未完成、未取消
    const urgentableDishes = currentOrderDishes.value
      .filter(canWaiterKitchenOperate)
      .filter((d) => d.status !== 'served' && d.status !== 'cancelled')
      .filter((d) => d.operateStatus === 'normal' && !isDishUrgent(d))
      .filter((d) => d.originalStatus !== 8)
      .filter((d) => d.orderFoodId); // 确保有orderFoodId

    if (urgentableDishes.length === 0) {
      showToast('暂无可以加急的菜品');
      return;
    }

    confirmMessage.value = `确定要对 ${urgentableDishes.length} 道菜品设置加急处理吗？`;
    confirmTitle.value = '批量加急确认';
    if (await confirmDialog.value?.show()) {
      try {
        if (!(await loadKitchenMakingState())) {
          showToast('制作状态未确认，请稍后再试');
          return;
        }
        const eligibleDishes = urgentableDishes.filter(canWaiterKitchenOperate);
        if (!eligibleDishes.length) {
          showToast('菜品已制作完成，暂无可以加急的菜品');
          return;
        }
        const promises = eligibleDishes.map((dish) => sendUrgent(dish));

        const results = await Promise.allSettled(promises);
        const successCount = results.filter((result) => result.status === 'fulfilled').length;

        if (successCount > 0) {
          showToast(`已成功对 ${successCount} 道菜品设置加急处理`);
        } else {
          showToast('批量加急操作失败，请稍后重试');
        }
      } catch (error) {
        console.error('批量加急失败:', error);
        showToast('批量加急操作失败，请稍后重试');
      } finally {
        await loadOrderDetail();
      }
    }
  };

  const batchPauseDishes = async (): Promise<void> => {
    if (waiterPauseBusy.value) return;
    if (order.value.orderStatus === 1) {
      showToast('订单尚未确认，无法进行批量暂停操作');
      return;
    }
    const candidates = currentOrderDishes.value.filter(canWaiterPause);
    if (!candidates.length) {
      showToast('暂无尚未开工且可以暂停的菜品');
      return;
    }
    waiterPauseBusy.value = true;
    try {
      confirmMessage.value = `确定要暂停 ${candidates.length} 道尚未开工的菜品吗？`;
      confirmTitle.value = '批量暂停确认';
      if (!(await confirmDialog.value?.show())) return;
      if (!(await loadKitchenMakingState())) {
        showToast('制作状态未确认，请稍后再试');
        return;
      }
      const pausableDishes = candidates.filter(canWaiterPause);
      if (!pausableDishes.length) {
        showToast('菜品已开工或状态已变化，不能暂停');
        return;
      }
      const results = await Promise.allSettled(
        pausableDishes.map((dish) =>
          changeOperateStatus({
            orderId: orderId.value,
            orderFoodId: dish.orderFoodId,
            shopId: shopId.value,
            operateStatus: 1,
          }),
        ),
      );
      const successCount = results.filter(
        (result) => result.status === 'fulfilled' && result.value.code === 200,
      ).length;
      const skippedCount = candidates.length - pausableDishes.length;
      showToast(
        `已暂停 ${successCount} 道菜${
          skippedCount ? `，跳过 ${skippedCount} 道状态已变化的菜品` : ''
        }${successCount < pausableDishes.length ? '，部分操作失败，请重试' : ''}`,
      );
      await loadOrderDetail();
    } catch (error) {
      console.error('批量暂停失败:', error);
      showToast('批量暂停操作失败，请稍后重试');
    } finally {
      waiterPauseBusy.value = false;
    }
  };

  const batchResumeDishes = async () => {
    // 检查订单状态
    if (order.value.orderStatus === 1) {
      showToast('订单尚未确认，无法进行批量开始操作');
      return;
    }

    // 筛选可以恢复的菜品：暂停状态且未上菜未取消
    const resumableDishes = currentOrderDishes.value
      .filter(canWaiterKitchenOperate)
      .filter((d) => d.operateStatus === 'paused')
      .filter((d) => d.status !== 'served' && d.status !== 'cancelled')
      .filter((d) => d.orderFoodId); // 确保有orderFoodId

    if (resumableDishes.length === 0) {
      showToast('暂无可以开始的菜品');
      return;
    }

    confirmMessage.value = `确定要开始 ${resumableDishes.length} 道菜品吗？`;
    confirmTitle.value = '批量开始确认';
    if (await confirmDialog.value?.show()) {
      try {
        if (!(await loadKitchenMakingState())) {
          showToast('制作状态未确认，请稍后再试');
          return;
        }
        // 批量调用恢复接口
        const eligibleDishes = resumableDishes.filter(canWaiterKitchenOperate);
        if (!eligibleDishes.length) {
          showToast('菜品已制作完成，暂无可以开始的菜品');
          return;
        }
        const promises = eligibleDishes.map((dish) =>
          changeOperateStatus({
            orderId: orderId.value,
            orderFoodId: dish.orderFoodId,
            shopId: shopId.value,
            operateStatus: 0, // Swagger：0 表示正常
          }),
        );

        const results = await Promise.allSettled(promises);
        const successCount = results.filter(
          (result) => result.status === 'fulfilled' && result.value.code === 200,
        ).length;

        if (successCount > 0) {
          showToast(`已开始 ${successCount} 道菜品`);
          // 重新加载订单详情以获取最新状态
          await loadOrderDetail();
        } else {
          showToast('批量开始操作失败，请稍后重试');
        }
      } catch (error) {
        console.error('批量开始失败:', error);
        showToast('批量开始操作失败，请稍后重试');
      }
    }
  };

  const confirmOrderInDetails = async () => {
    // 检查营业时间
    const isBusinessTime = await checkBusinessTime();
    if (!isBusinessTime) {
      return;
    }

    // 检查当前订单状态
    if (order.value.orderStatus !== 1) {
      showToast('订单状态不正确，无法确认');
      return;
    }

    confirmMessage.value = `确定要确认 ${tableName.value} 的订单吗？\n\n确认后订单将发送至厨房开始制作。`;
    confirmTitle.value = '确认订单';
    if (await confirmDialog.value?.show()) {
      try {
        const response = await auditOrder({
          orderId: orderId.value,
          auditStatus: 2, // 2表示确认/审核通过
          shopId: shopId.value,
          remark: `服务员${getEmployeeName()}确认订单`,
        });

        if (response.code === 200) {
          showToast(`${tableName.value}订单已确认！`);
          // 刷新订单详情
          await loadOrderDetail();
        } else {
          showToast(response.msg || '订单确认失败');
        }
      } catch (error) {
        console.error('确认订单失败:', error);
        showToast('确认订单失败，请稍后重试');
      }
    }
  };

  const proceedToCheckout = () => {
    router.push(
      `/orderCheckOut?table=${tableNumber.value}&orderId=${orderId.value}&from=orderDetail&shopId=${shopId.value}`,
    );
  };

  const showOrderHistory = () => {
    router.push('/orderHistory');
  };

  const showServingSuccess = (dishName: string) => {
    // 简化版成功提示
    showToast(`${dishName} 已上菜！`);
  };

  const loadAllCategories = async () => {
    try {
      const [categoryTagResp, itemResp] = await Promise.all([
        fetchCategoryTagList({
          // 新接口不分页，只按店铺查询分类+标签
          shopId: shopId.value || 1,
        }),
        fetchCategoryItemList({
          pageNum: 1,
          pageSize: 999,
          data: { shopId: shopId.value || 1, shopFoodStatus: 0 },
        }),
      ]);

      if (categoryTagResp.code === 200 && itemResp.code === 200) {
        // 分类+标签接口返回数组，为兼容性同时兼容 result.list 结构
        const categoryTagResult: any = categoryTagResp.result;
        const categoryTagList: any[] = Array.isArray(categoryTagResult)
          ? categoryTagResult
          : categoryTagResult?.list || [];
        const foodList: any[] = itemResp.result?.list || [];

        // 先构建所有菜品的完整列表
        const allItems: Array<CategoryItemModel> = [];
        foodList.forEach((item: any) => {
          const specItems: Array<{
            id: number;
            name: string;
            price: number;
            specId: number;
            specName: string;
          }> = [];
          (item.specList || []).forEach((specItem: any) => {
            specItems.push({
              id: specItem.shopFoodSpecId,
              name: specItem.specName,
              price: specItem.price,
              specId: specItem.specId,
              specName: specItem.specName,
            });
          });
          allItems.push({
            id: item.shopFoodId,
            name: item.shopFoodName,
            foodId: item.foodId,
            foodName: item.foodName,
            shopFoodStatus: item.shopFoodStatus,
            price: item.price,
            description: item.shopFoodDesc,
            foodType: item.foodType,
            image: item.imageUrl,
            specs: specItems,
            shopFoodSpecId: specItems.length > 0 ? specItems[0].id : item.shopFoodId,
            cookList: item.cookList || [], // 添加cookList字段
          });
        });
        allMenuItems.value = allItems;

        // 再清空分类并重新构建
        categories.value = [];

        // 「全部」分类
        categories.value.push({
          id: 0,
          name: '全部',
          items: [],
        });

        // 根据返回的 type 构建分类：
        // type = 1 -> 标签：按菜品的 tags 字段模糊匹配
        // type = 2 -> 分类：按原来的分类匹配逻辑（shopFoodCategoryId === id）
        categoryTagList.forEach((ct: any) => {
          const ctId = ct.id;
          const ctName = ct.name;
          const ctType = ct.type; // 1=标签，2=分类

          let originItemList: any[] = [];
          if (ctType === 2) {
            // 分类：按分类ID匹配
            originItemList = foodList.filter((item: any) => item.shopFoodCategoryId === ctId);
          } else if (ctType === 1) {
            // 标签：按 tags 字段模糊匹配
            originItemList = foodList.filter((item: any) => {
              const tagsStr = (item.tags || '') as string;
              return tagsStr
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .includes(ctName);
            });
          } else {
            // 其他类型暂不处理
            return;
          }

          // 当前分类/标签下的菜品列表（在 allItems 中按 shopFoodId 过滤）
          const items: Array<CategoryItemModel> = allItems.filter((it) =>
            originItemList.some((origin) => origin.shopFoodId === it.id),
          );

          categories.value.push({
            id: ctId,
            name: ctName,
            categoryType: ctType,
            items: items,
          });
        });

        if (categories.value.length > 0) {
          selectedCategory.value = categories.value[0].name || '';
        }
      }
    } catch (error) {
      console.error('加载菜品分类失败：', error);
    }
  };

  const adjustServiceFee = () => {
    // 实现服务费调整逻辑
    console.log('服务费调整逻辑');
  };

  // 编辑折扣金额
  const editDiscountAmount = () => {
    // 检查剩余应付金额 - 如果没有剩余应付金额或金额为0，不允许编辑折扣金额
    if (order.value.remainAmount !== undefined && order.value.remainAmount <= 0) {
      showToast('订单已结清，无法修改折扣金额');
      return;
    }

    tempDiscountAmount.value = discountAmount.value;
    tempDiscountRemark.value = '';
    discountModalVisible.value = true;
  };

  // 保存折扣金额
  const saveDiscountAmount = async () => {
    try {
      // 验证折扣金额不能大于剩余应付金额
      if (
        order.value.remainAmount !== undefined &&
        tempDiscountAmount.value > order.value.remainAmount
      ) {
        showDialog({
          message: `折扣金额不能大于剩余应付金额 ¥${order.value.remainAmount.toFixed(2)}`,
        });
        return;
      }

      // 获取工号
      const employeeId = getEmployeeId();

      // 构建备注信息（工号 + 用户输入的备注）
      const remark = employeeId + (tempDiscountRemark.value ? ': ' + tempDiscountRemark.value : '');

      // 调用接口
      const response = await editAmount({
        orderId: orderId.value,
        discountAmount: tempDiscountAmount.value,
        remark: remark,
      });

      if (response.code === 200) {
        discountAmount.value = tempDiscountAmount.value;
        discountModalVisible.value = false;
        showDialog({ message: '折扣金额设置成功' });
      } else {
        showDialog({ message: response.msg || '设置失败，请稍后重试' });
      }
    } catch (error) {
      console.error('设置折扣金额失败:', error);
      showDialog({ message: '设置失败，请稍后重试' });
    }
  };

  // 取消编辑折扣金额
  const cancelEditDiscount = () => {
    discountModalVisible.value = false;
    tempDiscountAmount.value = 0;
    tempDiscountRemark.value = '';
  };

  // 显示支付确认模态框
  const showPaymentConfirmModal = () => {
    selectedPaymentMethod.value = 4; // 默认选择POS支付
    paymentRemark.value = '';
    paymentConfirmModalVisible.value = true;
  };

  // 隐藏支付确认模态框
  const hidePaymentConfirmModal = () => {
    paymentConfirmModalVisible.value = false;
    selectedPaymentMethod.value = 4;
    paymentRemark.value = '';
  };

  // 确认支付
  const confirmPayment = async () => {
    try {
      if (!order.value.orderId) {
        showToast('订单信息不完整，无法确认支付');
        return;
      }

      const employeeName = getEmployeeName();
      let remark = '';
      let paymentMethodName = '';

      // 根据选择的支付方式设置备注和支付方式名称
      if (selectedPaymentMethod.value === 4) {
        paymentMethodName = 'POS支付';
        remark = `服务员${employeeName}确认POS支付完成，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 5) {
        paymentMethodName = '公司挂账';
        remark = `服务员${employeeName}确认挂账，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 7) {
        paymentMethodName = '现金支付';
        remark = `服务员${employeeName}确认现金支付完成，${paymentRemark.value || ''}`;
      }

      const paymentData: OrderPaymentModel = {
        orderId: order.value.orderId,
        paymentMethod: selectedPaymentMethod.value,
        paymentMethodName: paymentMethodName,
        paymentStatus: 1, // 已支付
        amount: order.value.remainAmount || 0,
        paymentTime: dateTimeFormat(dayjs()),
        remark: remark.trim(),
      };

      const response = await insertOrderPayment(paymentData);
      if (response.code === 200) {
        hidePaymentConfirmModal();
        showToast(`${tableName.value}${paymentMethodName}已确认完成！`);
        // 刷新订单详情
        await loadOrderDetail();
      } else {
        showToast(response.msg || '确认支付失败');
      }
    } catch (error) {
      console.error('确认支付失败:', error);
      showToast('确认支付失败，请稍后重试');
    }
  };

  // 限制只能输入数字（包含小数）
  const handleDiscountInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    // 只允许数字和小数点
    const numericValue = value.replace(/[^\d.]/g, '');
    // 确保只有一个小数点
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      target.value = parts[0] + '.' + parts.slice(1).join('');
    } else {
      target.value = numericValue;
    }
    tempDiscountAmount.value = parseFloat(target.value) || 0;
  };

  const decreaseItem = (name: string) => {
    const idx = orderItems.value.findIndex((item) => item.name === name);
    if (idx !== -1) {
      if (orderItems.value[idx].quantity > 1) {
        orderItems.value[idx].quantity -= 1;
      } else {
        orderItems.value.splice(idx, 1);
      }
    }
  };

  const clearOrder = async () => {
    confirmMessage.value = '确定要清空所有加菜项吗？';
    confirmTitle.value = '清空确认';
    if (await confirmDialog.value?.show()) {
      orderItems.value = [];
    }
  };

  const submitAddOrder = async () => {
    if (orderItems.value.length === 0) return;

    // 检查营业时间
    const canProceed = await checkBusinessTime();
    if (!canProceed) {
      return;
    }

    // 添加详细的调试信息
    console.log('=== 提交加菜订单调试信息 ===');
    console.log('orderItems:', orderItems.value);
    console.log('orderTotal:', orderTotal.value);
    console.log('totalOrderCount:', totalOrderCount.value);
    console.log('peopleCount:', peopleCount.value);
    console.log('tableName:', tableName.value);
    console.log('shopId:', shopId.value);
    console.log('orderId:', orderId.value);
    console.log('orderNo:', orderNo.value);
    console.log('order:', order.value);
    console.log('========================');

    try {
      // 构建符合接口要求的加菜订单数据
      const addFoodData = {
        orderId: orderId.value,
        shopId: shopId.value,
        tableId: tableNumber.value,
        tableUniqueCode: `TABLE_${tableNumber.value}`,
        userId: getEmployeeId(), // 使用保存的工号
        userName: getEmployeeName(), // 使用保存的工号，如果没有则使用默认值
        orderStatus: order.value.orderStatus || 1, // 保持原订单状态
        orderType: order.value.orderType || 1, // 保持原订单类型
        orderChannel: '服务员加菜', // 下单渠道
        dineType: order.value.dineType || 1, // 保持原就餐类型
        personCount: peopleCount.value, // 用餐人数
        totalAmount: orderTotal.value, // 加菜总金额
        discountAmount: 0, // 折扣金额
        foodAmount: orderTotal.value, // 菜品金额
        serviceAmount: 0, // 服务费
        packageAmount: 0, // 包装费
        description: '',
        // description: `${tableName.value}加菜，共${totalOrderCount.value}份菜品`,
        foodList: orderItems.value.map((item) => ({
          shopFoodId: item.id, // 使用菜品ID
          shopFoodSpecId: item.shopFoodSpecId, // 规格ID
          foodCookDesc: item.methodName || '', // 使用组合的methodName作为foodCookDesc
          quantity: item.quantity,
          price: item.price,
          amount: item.price * item.quantity,
          sourcePrice: item.price,
          sourceAmount: item.price * item.quantity,
          priority: 1, // 优先级
          addType: 2, // 添加类型：2-加菜
          addPerson: getEmployeeName(), // 使用保存的工号
        })),
        paymentList: [], // 支付列表，加菜时为空
      };

      console.log('完整加菜订单数据:', addFoodData);

      // 调用加菜接口
      const response = await fetchAddFoodWithOrder(addFoodData);
      if (response.code === 200) {
        console.log('加菜成功:', response.result);
        showToast(
          `${tableName.value}加菜订单已提交！共${totalOrderCount.value}份菜品，金额：¥${orderTotal.value}`,
        );

        // 清空加菜列表
        orderItems.value = [];

        // 重新加载订单详情
        await loadOrderDetail();

        // 切换回已点菜品视图
        currentView.value = 'ordered';
      } else {
        showToast(response.msg || '加菜失败，请稍后重试');
      }
    } catch (error) {
      console.error('提交加菜订单失败:', error);
      showToast('提交加菜订单失败，请稍后重试');
    }
  };

  const refreshKitchenOnResume = (): void => {
    if (document.visibilityState === 'visible' && currentView.value === 'ordered') {
      void loadKitchenMakingState();
    }
  };

  onMounted(async () => {
    if (props.detailOrder) {
      tableNumber.value = props.detailOrder.tableId || 0;
      tableName.value = props.detailOrder.tableName || '';
      shopId.value = props.detailOrder.shopId || 0;
      shopName.value = props.detailOrder.shopName || '';
      orderId.value = props.detailOrder.orderId || 0;
    } else {
      tableNumber.value = Number(route.query.tableNumber) || 0;
      tableName.value = (route.query.tableName as string) || '';
      shopId.value = Number(route.query.shopId) || 0;
      shopName.value = (route.query.shopName as string) || '';
      orderId.value = Number(route.query.orderId) || 0;
    }
    document.addEventListener('visibilitychange', refreshKitchenOnResume);
    window.addEventListener('focus', refreshKitchenOnResume);
    kitchenStatusTimer = setInterval(() => {
      if (currentView.value === 'ordered') void loadKitchenMakingState();
    }, 10000);

    // 加载订单详情
    await loadOrderDetail();
    // 加载店铺详情
    await loadShopDetail();
    // 移除了loadAllCategories()调用，改为在切换到加菜tab时才加载
  });

  // 组件卸载时清理资源
  onUnmounted(() => {
    kitchenStatusDisposed = true;
    document.removeEventListener('visibilitychange', refreshKitchenOnResume);
    window.removeEventListener('focus', refreshKitchenOnResume);
    if (kitchenStatusTimer) clearInterval(kitchenStatusTimer);
    stopScan();
  });

  // 新增：判断是否可以去结账
  const canProceedToCheckout = () => {
    // 如果订单已取消，不能去结账
    if (order.value.orderStatus === 999) {
      return false;
    }

    // 只有线上支付才显示去结账按钮
    if (!isOnlinePayment.value) {
      return false;
    }

    // 检查支付状态 - 如果有支付状态名称，根据支付状态判断
    if (order.value.paymentStatusName) {
      const statusLower = order.value.paymentStatusName.toLowerCase();
      // 如果支付状态包含"已支付"、"支付成功"等关键词，不显示去结账按钮
      if (
        statusLower.includes('已支付') ||
        statusLower.includes('支付成功') ||
        statusLower.includes('paid') ||
        statusLower.includes('success')
      ) {
        return false;
      }
    }

    // 检查剩余应付金额 - 如果没有剩余应付金额或金额为0，不显示去结账按钮
    if (order.value.remainAmount !== undefined && order.value.remainAmount <= 0) {
      return false;
    }

    // 检查订单状态 - 订单必须是已完成(10)或其他可结账状态，且有待支付金额
    if (order.value.orderStatus === 10) {
      // 已完成状态下，只有还有剩余金额才能去结账
      return order.value.remainAmount && order.value.remainAmount > 0;
    }

    // 其他状态默认可以去结账
    return true;
  };

  // 按属性名称分组的计算属性
  const groupedMethods = computed(() => {
    const groups: { [key: string]: any[] } = {};
    methods.value.forEach((method) => {
      const attributeName = method.attributeName || method.cookName || '做法';
      if (!groups[attributeName]) {
        groups[attributeName] = [];
      }
      groups[attributeName].push(method);
    });
    return groups;
  });

  // 从登录态获取工号
  const getEmployeeId = () => {
    return employeeId.value;
  };

  // 从登录态获取员工姓名
  const getEmployeeName = () => {
    return employeeName.value;
  };

  // 店铺详情相关状态
  const shopDetail = ref<ShopDetailModel | null>(null);
  const isOnlinePayment = ref(false); // 是否为线上支付
  const businessTimeList = ref<Array<{ beginTime: string; endTime: string }>>([]);

  // 获取店铺详情
  const loadShopDetail = async () => {
    if (!shopId.value) return;

    try {
      const response = await fetchShopDetail({ shopId: shopId.value });
      if (response.code === 200 && response.result) {
        shopDetail.value = response.result;
        // 判断支付方式：0线下、1线上
        isOnlinePayment.value = response.result.paymentLine === 1;
        // 保存营业时间列表
        businessTimeList.value = response.result.businessTimeList || [];
        console.log('店铺详情加载成功:', response.result);
        console.log('支付方式:', isOnlinePayment.value ? '线上' : '线下');
        console.log('营业时间:', businessTimeList.value);
      }
    } catch (error) {
      console.error('加载店铺详情失败:', error);
    }
  };

  // 检查当前时间是否在营业时间内
  const checkBusinessTime = async (): Promise<boolean> => {
    // 检查是否有营业时间配置
    if (!businessTimeList.value || businessTimeList.value.length === 0) {
      return true;
    }

    // 获取当前时间（HH:mm格式）
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(
      now.getMinutes(),
    ).padStart(2, '0')}`;

    // 检查是否在任何一个营业时间段内
    const isInBusinessTime = businessTimeList.value.some((timeSlot) => {
      return currentTime >= timeSlot.beginTime && currentTime <= timeSlot.endTime;
    });

    if (isInBusinessTime) {
      return true;
    }

    // 不在营业时间内，显示确认对话框
    const timeSlots = businessTimeList.value
      .map((slot) => `${slot.beginTime}-${slot.endTime}`)
      .join('、');

    confirmMessage.value = `当前时间 ${currentTime} 不在营业时间内。\n\n营业时间：${timeSlots}\n\n确定要继续操作吗？`;
    confirmTitle.value = '营业时间提醒';
    const result = await confirmDialog.value?.show();
    return result || false;
  };

  // 确认支付完成（线下支付）
  const confirmPaymentComplete = async () => {
    if (!order.value.orderId) {
      showToast('订单信息不完整，无法确认支付');
      return;
    }

    // 显示支付确认模态框
    showPaymentConfirmModal();
  };

  // 显示扫码支付全屏界面
  const showScanPaymentModal = async () => {
    scanPaymentModalVisible.value = true;
    manualScanCode.value = '';
    scanPaymentRemark.value = '';
    scanStatus.value = '';
    // 立即设置为扫描中，避免显示"正在启动摄像头"提示
    isScanning.value = true;
    manualInputModalVisible.value = false;

    // 自动开始扫描
    await nextTick();
    getCamerasInfo();
  };

  // 隐藏扫码支付全屏界面
  const hideScanPaymentModal = () => {
    stopScan();
    scanPaymentModalVisible.value = false;
    manualScanCode.value = '';
    scanPaymentRemark.value = '';
    scanStatus.value = '';
    manualInputModalVisible.value = false;
  };

  // 显示手动输入模态框
  const showManualInputModal = () => {
    manualInputModalVisible.value = true;
  };

  // 隐藏手动输入模态框
  const hideManualInputModal = () => {
    manualInputModalVisible.value = false;
  };

  // 获取摄像头信息并初始化
  const getCamerasInfo = () => {
    // 立即设置为扫描中，隐藏"正在启动摄像头"提示
    isScanning.value = true;

    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          html5QrCode = new Html5Qrcode('qr-reader');
          startScan();
        } else {
          // 没有可用摄像头
          isScanning.value = false;
          scanStatus.value = '未检测到摄像头设备';
          scanStatusClass.value = 'bg-red-100 text-red-700';
        }
      })
      .catch((err) => {
        console.error('获取摄像头失败:', err);
        scanStatus.value = '摄像头无访问权限！';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        isScanning.value = false;
      });
  };

  // 开始扫描
  const startScan = () => {
    if (!html5QrCode) {
      console.error('html5QrCode 未初始化');
      return;
    }

    isScanning.value = true;
    scanStatus.value = '';
    scanStatusClass.value = '';

    html5QrCode
      .start(
        { facingMode: 'environment' },
        {
          fps: 20,
          // 全屏扫码：使用整个视窗大小
          qrbox: function (viewfinderWidth, viewfinderHeight) {
            return {
              width: viewfinderWidth,
              height: viewfinderHeight,
            };
          },
          // 不限制宽高比，允许全屏显示
          aspectRatio: undefined,
        },
        (decodedText, decodedResult) => {
          // 扫码成功回调
          handleScanResult(decodedText);
        },
      )
      .then(() => {
        // 扫描启动成功后，强制应用全屏样式
        nextTick(() => {
          forceFullscreenStyles();
        });
      })
      .catch((err) => {
        console.error('扫码失败', err);
        scanStatus.value = '启动摄像头失败，请检查权限设置';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        isScanning.value = false;
      });
  };

  // 强制应用全屏样式
  const forceFullscreenStyles = () => {
    const qrReader = document.getElementById('qr-reader');
    if (!qrReader) return;

    // 强制设置容器样式
    const container = qrReader.querySelector('#html5qr-container');
    if (container) {
      (container as HTMLElement).style.width = '100%';
      (container as HTMLElement).style.height = '100%';
      (container as HTMLElement).style.minWidth = '100%';
      (container as HTMLElement).style.minHeight = '100%';
    }

    // 强制设置视频样式
    const videos = qrReader.querySelectorAll('video');
    videos.forEach((video) => {
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.minWidth = '100%';
      video.style.minHeight = '100%';
      video.style.objectFit = 'cover';
      video.style.position = 'absolute';
      video.style.top = '0';
      video.style.left = '0';
      video.style.right = '0';
      video.style.bottom = '0';
    });

    // 强制设置画布样式
    const canvases = qrReader.querySelectorAll('canvas');
    canvases.forEach((canvas) => {
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.minWidth = '100%';
      canvas.style.minHeight = '100%';
      canvas.style.objectFit = 'cover';
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.right = '0';
      canvas.style.bottom = '0';
    });
  };

  // 停止扫描
  const stopScan = () => {
    if (html5QrCode && html5QrCode.isScanning) {
      html5QrCode
        .stop()
        .then(() => {
          html5QrCode?.clear();
          isScanning.value = false;
        })
        .catch((err) => {
          console.error('停止扫描失败:', err);
          isScanning.value = false;
        });
    } else {
      isScanning.value = false;
    }
  };

  // 处理扫描结果
  const handleScanResult = async (scanCode: string) => {
    // 验证扫码结果
    if (!scanCode || scanCode.trim() === '') {
      return;
    }

    // 停止扫描，但保持界面打开
    stopScan();

    // 显示处理中状态
    scanStatus.value = '扫码成功，正在处理支付...';
    scanStatusClass.value = 'bg-blue-100 text-blue-700';

    // 调用支付接口
    await submitScanPayment(scanCode);
  };

  // 处理手动输入的扫码结果
  const handleManualScan = async () => {
    if (!manualScanCode.value || manualScanCode.value.trim() === '') {
      showToast('请输入扫码结果');
      return;
    }

    const scanCode = manualScanCode.value.trim();

    // 关闭手动输入模态框
    manualInputModalVisible.value = false;

    // 停止扫描，但保持界面打开
    stopScan();

    // 显示处理中状态
    scanStatus.value = '正在处理支付...';
    scanStatusClass.value = 'bg-blue-100 text-blue-700';

    // 调用支付接口
    await submitScanPayment(scanCode);
  };

  // 提交扫码支付
  const submitScanPayment = async (scanCode: string) => {
    try {
      if (!order.value.orderId) {
        scanStatus.value = '订单信息不完整，无法支付';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        showToast('订单信息不完整，无法支付');
        return;
      }

      // 获取IP地址（简化处理，实际可以从服务器获取）
      const ip = await getClientIP();

      // 构建支付数据
      const paymentData = {
        orderId: order.value.orderId,
        scanCode: scanCode,
        remark: scanPaymentRemark.value || '',
        ip: ip || '',
        amount: order.value.remainAmount || 0,
      };
      scanPayment;
      // 调用扫码支付接口
      const response = await scanPayment(paymentData);

      // 根据接口返回结果处理
      if (response.code === 200) {
        // API 返回 200，立即关闭扫码界面
        hideScanPaymentModal();

        // 根据业务结果在详情界面显示提示
        if (response.result?.success) {
          // 支付成功，刷新订单详情
          await loadOrderDetail();
          showToast('扫码支付成功！');
        } else {
          // 支付失败，显示确认模态框
          scanPaymentFailErrorMsg.value = response.result?.errorMsg || '';
          scanPaymentFailModalVisible.value = true;
        }
      } else {
        // API 返回非 200，保持界面打开
        const errorMsg = response.msg || '支付失败，请重试';
        scanStatus.value = errorMsg;
        scanStatusClass.value = 'bg-red-100 text-red-700';
        showToast(errorMsg);

        // 重新开始扫描，允许用户继续扫码
        await nextTick();
        getCamerasInfo();
      }
    } catch (error) {
      console.error('扫码支付失败:', error);

      // 显示错误信息，保持界面打开
      scanStatus.value = '支付失败，请稍后重试';
      scanStatusClass.value = 'bg-red-100 text-red-700';
      showToast('支付失败，请稍后重试');

      // 重新开始扫描，允许用户继续扫码
      await nextTick();
      getCamerasInfo();
    }
  };

  // 获取客户端IP地址（简化版本）
  const getClientIP = async (): Promise<string> => {
    // 可以调用后端接口获取真实IP，这里先返回空字符串
    // 或者使用第三方服务获取IP
    return '';
  };

  // 确认已经支付（刷新扫码支付状态）
  const confirmScanPaymentPaid = async () => {
    // 防抖：如果正在刷新，直接返回
    if (isRefreshingPayment.value) {
      return;
    }

    try {
      if (!order.value.orderId) {
        showToast('订单信息不完整，无法确认支付');
        return;
      }

      // 设置 loading 状态
      isRefreshingPayment.value = true;

      const response = await refreshScanPayment({
        orderId: order.value.orderId,
      });

      if (response.code === 200) {
        // 根据业务结果处理
        if (response.result?.success) {
          // 支付成功，关闭模态框并刷新订单详情
          scanPaymentFailModalVisible.value = false;
          scanPaymentFailErrorMsg.value = '';
          showToast('支付成功！');
          // 重新查询订单详情
          await loadOrderDetail();
        } else {
          // 支付失败，显示错误信息，模态框不关闭
          const errorMsg = response.result?.errorMsg || '刷新支付状态失败，请重试';
          scanPaymentFailErrorMsg.value = errorMsg;
          showToast(errorMsg);
        }
      } else {
        // API 返回非 200，显示错误信息，模态框不关闭
        const errorMsg = response.msg || '刷新支付状态失败，请稍后重试';
        scanPaymentFailErrorMsg.value = errorMsg;
        showToast(errorMsg);
      }
    } catch (error) {
      console.error('刷新支付状态失败:', error);
      const errorMsg = '刷新支付状态失败，请稍后重试';
      scanPaymentFailErrorMsg.value = errorMsg;
      showToast(errorMsg);
    } finally {
      // 无论成功失败，都要重置 loading 状态
      isRefreshingPayment.value = false;
    }
  };

  // 取消确认支付
  const cancelScanPaymentConfirm = () => {
    scanPaymentFailModalVisible.value = false;
    scanPaymentFailErrorMsg.value = '';
    isRefreshingPayment.value = false; // 重置 loading 状态
  };

  // 新增：判断是否可以显示确认支付完成按钮
  const canShowConfirmPayment = () => {
    // 如果订单已取消，不能确认支付
    if (order.value.orderStatus === 999) {
      return false;
    }

    // 检查支付状态 - 如果已经支付，不显示确认支付按钮
    if (order.value.paymentStatusName) {
      const statusLower = order.value.paymentStatusName.toLowerCase();
      if (
        statusLower.includes('已支付') ||
        statusLower.includes('支付成功') ||
        statusLower.includes('paid') ||
        statusLower.includes('success')
      ) {
        return false;
      }
    }

    // 检查剩余应付金额 - 如果没有剩余应付金额或金额为0，不显示确认支付按钮
    // if (order.value.remainAmount !== undefined && order.value.remainAmount <= 0) {
    //   return false;
    // }

    // 订单状态为已完成(10)或其他可支付状态，且有待支付金额，则可以显示确认支付完成按钮
    return (
      order.value.orderStatus === 10 ||
      order.value.paymentStatus != 10 ||
      (order.value.remainAmount && order.value.remainAmount > 0)
    );
  };

  // 显示提示消息
  const showToast = (message: string): void => {
    showVantToast({ message, position: 'top', duration: 5000 });
  };

  // 确认对话框相关
  const confirmDialog = ref<InstanceType<typeof ConfirmDialog>>();
  const confirmMessage = ref('');
  const confirmTitle = ref('');

  // 新增：判断是否可以编辑折扣金额
  const canEditDiscountAmount = () => {
    // 检查剩余应付金额 - 如果没有剩余应付金额或金额为0，不允许编辑折扣金额
    if (order.value.remainAmount !== undefined && order.value.remainAmount <= 0) {
      return false;
    }

    return true;
  };

  const toggleMethodSelection = (attributeName: string, methodId: number) => {
    // 找到对应的cookType
    const method = methods.value.find((m) => m.id === methodId);
    const cookType = method?.cookType || 1; // 默认为多选

    if (cookType === 2) {
      // 单选：直接比较
      selectedMethods.value[attributeName] = methodId;
    } else {
      // 多选：支持多个选择
      if (!selectedMethods.value[attributeName]) {
        // 如果该属性还没有选择，初始化为数组
        selectedMethods.value[attributeName] = [methodId];
      } else if (Array.isArray(selectedMethods.value[attributeName])) {
        // 如果已经是数组，切换选择状态
        const currentSelection = selectedMethods.value[attributeName] as number[];
        const index = currentSelection.indexOf(methodId);
        if (index > -1) {
          // 如果已选中，则取消选择
          currentSelection.splice(index, 1);
          // 如果数组为空，删除该属性
          if (currentSelection.length === 0) {
            delete selectedMethods.value[attributeName];
          }
        } else {
          // 如果未选中，则添加选择
          currentSelection.push(methodId);
        }
      } else {
        // 如果之前是单选，转换为多选
        const currentId = selectedMethods.value[attributeName] as number;
        if (currentId === methodId) {
          // 如果点击的是已选中的，则取消选择
          delete selectedMethods.value[attributeName];
        } else {
          // 否则转换为多选
          selectedMethods.value[attributeName] = [currentId, methodId];
        }
      }
    }
  };

  const isMethodSelected = (attributeName: string, methodId: number): boolean => {
    const selection = selectedMethods.value[attributeName];
    // 找到对应的cookType
    const method = methods.value.find((m) => m.id === methodId);
    const cookType = method?.cookType || 1; // 默认为多选

    if (cookType === 2) {
      // 单选：直接比较
      return selection === methodId;
    } else {
      // 多选：检查是否在数组中
      if (Array.isArray(selection)) {
        return selection.includes(methodId);
      }
      return selection === methodId;
    }
  };
</script>

<style scoped>
  .order-summary {
    text-align: left;
    line-height: 1.5;
  }

  .order-dish-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 112px 210px;
    align-items: center;
    gap: 12px;
  }

  .order-dish-labels > div {
    margin-top: 0;
  }

  .order-dish-serve > * {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .order-dish-serve i {
    flex-shrink: 0;
  }

  .order-detail-page p {
    margin: 0;
  }

  @media (max-width: 639px) {
    .order-dish-row {
      grid-template-columns: minmax(0, 1fr) 112px;
      gap: 8px;
    }

    .order-dish-info {
      grid-column: 1 / -1;
    }

    .order-dish-serve {
      grid-column: 2;
      grid-row: 2;
    }

    .order-dish-actions {
      grid-column: 1;
      grid-row: 2;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
    }
  }

  .order-kitchen-progress {
    display: grid;
    gap: 3px;
    margin-top: 4px;
    text-align: left;
  }

  .order-kitchen-task {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
  }

  .order-kitchen-status {
    padding: 2px 6px;
    border-radius: 6px;
    background: #e2e8f0;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
  }

  .order-kitchen-status[data-status='5'] {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .order-kitchen-status[data-status='9'] {
    background: #ffedd5;
    color: #c2410c;
  }

  .order-kitchen-status[data-status='10'] {
    background: #dcfce7;
    color: #15803d;
  }

  .order-kitchen-status[data-status='999'] {
    background: #fee2e2;
    color: #b91c1c;
  }

  .order-kitchen-assignee {
    color: #64748b;
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  /* 视图切换标签样式 */
  .view-tab-btn {
    font-size: 16px;
    color: #6b7280;
    background: transparent;
    border: none;
    cursor: pointer;
  }

  .view-tab-btn.active {
    background: #3b82f6;
    color: white;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
  }

  .view-tab-btn:hover:not(.active) {
    background: #f3f4f6;
    color: #374151;
  }

  /* 已点菜品样式 */
  .current-order-item {
    transition: all 0.3s ease;
  }

  .current-order-item:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  /* 操作按钮样式 */
  .current-order-item button {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border: none;
    cursor: pointer;
    font-size: 18px;
    margin: 0 2px;
    background-color: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(4px);
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .current-order-item button:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    background-color: rgba(255, 255, 255, 0.95);
  }

  /* 上菜按钮特殊样式 */
  .current-order-item .serve-btn-large {
    width: auto;
    height: 44px;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: white;
    padding: 8px 12px;
    border: none;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
    transition: all 0.3s ease;
    min-width: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .serve-btn-large:hover {
    background: linear-gradient(135deg, #16a34a, #15803d);
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 8px 20px rgba(34, 197, 94, 0.4);
  }

  /* 已上菜状态徽章样式 */
  .served-status-badge {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 600;
    min-width: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    opacity: 0.8;
    cursor: default;
  }

  /* 已取消状态徽章样式 */
  .cancelled-status-badge {
    background: linear-gradient(135deg, #6b7280, #4b5563);
    color: white;
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 600;
    min-width: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
    opacity: 0.8;
    cursor: default;
  }

  /* 批量操作按钮样式 */
  .batch-operation-btn {
    padding: 6px 10px;
    border-radius: 8px;
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 64px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .batch-operation-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  /* 分类样式 */
  .category-item {
    padding: 16px 12px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: all 0.2s;
  }

  .category-item.active {
    background-color: #f0fff4;
    color: #10b981;
    font-weight: 500;
    border-left: 3px solid #10b981;
  }

  .category-item:hover {
    background-color: #f8f8f8;
  }

  /* 菜品样式 */
  .menu-item {
    display: flex;
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
  }

  .menu-item:hover {
    background-color: #f9fafb;
  }

  .menu-item-image {
    width: 70px;
    height: 70px;
    border-radius: 8px;
    overflow: hidden;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .menu-item-content {
    flex: 1;
    text-align: left;
  }

  .menu-item-title {
    font-weight: 600;
    font-size: 20px;
    margin-bottom: 4px;
  }

  .menu-item-desc {
    font-size: 18px;
    color: #6b7280;
    margin-bottom: 8px;
    line-height: 1.3;
  }

  .menu-item-price {
    color: #ef4444;
    font-weight: 500;
    font-size: 20px;
  }

  .menu-item-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }

  .menu-item-btn.increase {
    background-color: #10b981;
    color: white;
  }

  .menu-item-btn.decrease {
    background-color: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
  }

  .menu-item-count {
    min-width: 40px;
    text-align: center;
    font-size: 20px;
  }

  .order-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .order-item-info {
    flex: 1;
    text-align: left;
  }

  .order-item-name {
    font-weight: 500;
    margin-bottom: 2px;
  }

  .order-item-price {
    font-size: 20px;
    color: #ef4444;
  }

  .order-item-controls {
    display: flex;
    align-items: center;
  }

  /* html5-qrcode 全屏样式 - 强制全屏显示，消除黑边 */
  .qr-reader-container {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
    overflow: hidden !important;
  }

  /* html5-qrcode 的所有内部容器都强制全屏 */
  .qr-reader-container > *,
  .qr-reader-container #html5qr-container,
  .qr-reader-container #html5qr-container > * {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
  }

  /* 视频元素 - 强制覆盖整个容器，消除黑边 */
  .qr-reader-container video,
  .qr-reader-container #html5qr-container video {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    object-fit: cover !important; /* 覆盖整个容器，裁剪多余部分 */
    object-position: center !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* 画布元素 - 用于二维码识别 */
  .qr-reader-container canvas,
  .qr-reader-container #html5qr-container canvas {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    object-fit: cover !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }

  /* html5-qrcode 的内部容器 */
  .qr-reader-container #html5qr-container {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    position: relative !important;
    overflow: hidden !important;
  }

  /* 隐藏扫描类型切换按钮 */
  .qr-reader-container #html5-qrcode-anchor-scan-type-change {
    display: none !important;
  }

  /* 隐藏扫描框遮罩区域 */
  .qr-reader-container #qr-shaded-region {
    display: none !important;
  }

  /* 隐藏扫描框边框 */
  .qr-reader-container #qr-shaded-region,
  .qr-reader-container #qr-shaded-region > * {
    display: none !important;
  }

  /* 动态扫描线 - 类似微信扫码效果 */
  .scan-line {
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0, 255, 0, 0.8) 50%,
      transparent 100%
    );
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.6), 0 0 20px rgba(0, 255, 0, 0.4);
    animation: scanMove 2s linear infinite;
  }

  @keyframes scanMove {
    0% {
      top: 0;
      opacity: 1;
    }
    50% {
      opacity: 1;
    }
    100% {
      top: 100%;
      opacity: 0.3;
    }
  }
</style>
